// Las 3 opciones (arranque ~60 %, exacta 100 % recomendada, holgada ~115 %; el vendedor escribe la cobertura o los paneles
// de cada una en "Ajustes del cálculo", Diego 12-oct) con su precio (subtotal + IVA + total),
// su rentabilidad con semáforo y sus motivos, ajustes a mano por opción, la mensualidad de REFERENCIA de FINSUS (ADENDA
// A0: enganche, mensualidad a 60 meses y tasa; el CAT solo en la leyenda chica), formas de pago y "Revisar con IA".
// Todo número sale del servidor (motor); la interfaz no calcula precios ni mensualidades.
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { BatteryCharging, Calculator, ChevronDown, Minus, Pencil, Plus, RefreshCw, Sparkles, Star, Trash2 } from 'lucide-react';
import { BANDA_COBERTURA, DESCUENTO_CONTADO_PCT, DESCUENTO_MAX_SIN_APROBACION_POR_OMISION, ETIQUETA_ESTRUCTURA, ETIQUETA_REGIMEN_FISCAL, FINSUS_REFERENCIA, MARCAS_INVERSOR_RED, OPCIONES, TEXTO_LEYENDA_FISCAL, TEXTO_SUPUESTO_RETORNO, fechaHoraCorta, numero, periodosEscritosSinRecibo, porqueEscritosVanComoRecibo, redondearCentavos, regimenNoDeduce, textoLeyendaFinsus } from '@cotizador/compartido';
import type { AjusteOpcion, Cotizacion, EdicionCampo, FinsusReferencia, FiscalCuestionario, NivelRespaldo, NivelRespaldoCalculado, OpcionCalculada, OpcionId, ParametrosCalculo, ResultadoMotor } from '@cotizador/compartido';
import { DESCUENTO_EFECTIVO_MAX_PCT, MSI_COSTO_BANCO_PCT, MSI_TOPE_LISTA_CON_IVA, PROMO_MSI_MESES, PROMO_PCT, contadoEnEfectivo, fechaLarga, listaDesdeContado, hoyEnHermosillo, mensualidadMsiPromo, motivoSinPromo, promoAlDia, promoDeHoy, promoDesconocida, promoVigente, textoTransformador } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { navegar } from '../../ruteo.tsx';
import { usarUsuario } from '../../sesion.tsx';
import { Boton, CampoArea, CampoSelect, CampoTexto, Chip, ErrorEnLinea, Interruptor, Vacio } from '../../componentes/ui.tsx';
import { ETIQUETA_OPCION, ETIQUETA_SEMAFORO, ETIQUETA_VEREDICTO, ahorroPorPeriodoImpreso, colorSemaforo, dinero, kwh, kwp, porcentaje, tasaIsr } from '../../logica/etiquetas.ts';
import { reemplazo } from '../../logica/ediciones.ts';
import { borradorCuestionario, conEnfoque, formaDesde, numeroDe } from '../../logica/cuestionario.ts';
import { panelesDelAjuste } from '../../logica/ajuste-paneles.ts';
import { CompetenciaOpcion } from './Competencia.tsx';
import { lecturaConDatos } from '../../logica/lectura.ts';
import { usarVistaPrecios } from '../../logica/vista-precios.ts';
import { usarCotizacion, usarRegenerarPdf } from './contexto.tsx';
import { GeneracionObra } from './Generacion.tsx';

/** Manda ediciones a /parametros y deja que el servidor recalcule. */
function usarEditarParametros() {
  const { ejecutar } = usarCotizacion();
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const mandar = async (hacer: (c: Cotizacion) => EdicionCampo[]) => {
    setOcupado(true);
    setError(null);
    try {
      await ejecutar((c) => api.editarBorrador(c.id, c.revision, hacer(c)));
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setOcupado(false);
    }
  };
  return { mandar, error, setError, ocupado };
}

/** Baterías de UN nivel de respaldo (E4b, 24-oct): el +/− de la tarjeta de cada opción (por opción) y el de la sección de
 *  niveles (resultado de antes). Escribe parametros.modulos_respaldo[nivel] (null = las que calcula el cotizador) y, si el
 *  vendedor no eligió par, fija el de HOY: los módulos van con ese par, para que un recálculo no los ponga en otra batería. */
function usarModulosRespaldo(r: ResultadoMotor, parametros: ParametrosCalculo) {
  const editar = usarEditarParametros();
  const fijados = parametros.modulos_respaldo ?? null;
  const eq = parametros.equipo_respaldo ?? null;
  const n0 = r.respaldo?.niveles[0] ?? null;
  const elegido = eq?.bateria_id && eq.inversor_id ? `${eq.bateria_id}|${eq.inversor_id}` : '';
  const ponerModulos = (nivel: NivelRespaldo, valor: number | null, motivo: string) =>
    void editar.mandar(() => {
      const nuevo = { esencial: null, confort: null, completa: null, ...(fijados ?? {}), [nivel]: valor };
      const despues = Object.values(nuevo).every((x) => x === null) ? null : nuevo;
      const ed = [reemplazo('/parametros/modulos_respaldo', fijados, despues, motivo)];
      // z48-2: la cantidad a mano manda; las horas escritas de esa opción se borran (para que no se lea otra cosa)
      const horas = parametros.horas_respaldo ?? null;
      if (horas?.[nivel] != null) {
        const h = { ...horas, [nivel]: null };
        ed.push(reemplazo('/parametros/horas_respaldo', horas, Object.values(h).every((x) => x === null) ? null : h, `${motivo} (sin horas escritas)`));
      }
      if (!elegido && n0?.bateria.id_tabla && n0.inversor?.id_tabla)
        ed.push(reemplazo('/parametros/equipo_respaldo', eq, { bateria_id: n0.bateria.id_tabla, inversor_id: n0.inversor.id_tabla }, 'Batería e inversor del respaldo (los de hoy)'));
      return ed;
    });
  return { ...editar, ponerModulos, fijados, eq, elegido };
}

/** E4b: con baterías POR OPCIÓN (`respaldo.por_opcion`), el nivel ligado a la opción con SU sistema (los paneles de la
 *  opción + sus baterías y su híbrido). null = la tarjeta de siempre (resultado de antes, solo respaldo o sin paneles). */
function nivelDeOpcion(r: ResultadoMotor, id: OpcionId): NivelRespaldoCalculado | null {
  const res = r.respaldo;
  if (!res?.por_opcion || res.tipo === 'respaldo') return null;
  return res.niveles.find((n) => n.opcion === id && n.sistema) ?? null;
}

/** Lo que trae margen: una opción de paneles o, por opción, el nivel con su sistema completo (paneles + baterías). */
type ConMargen = { rentabilidad?: OpcionCalculada['rentabilidad'] | null; semaforo_margen?: OpcionCalculada['semaforo_margen'] };

/** Promo de contado (Diego, 2-nov-2026): un toque la pone y otro la quita. La fecha sale de `PROMOS` (compartido), en hora de Hermosillo.
 *  Quien no puede editar la cotización no ve el botón (el servidor se lo rechazaría): con la promo puesta, solo el aviso. */
function BotonPromo({ opciones, recomendada, puedeEditar }: { opciones: readonly OpcionCalculada[]; recomendada: OpcionId; puedeEditar: boolean }) {
  const { cot, ejecutar } = usarCotizacion();
  const { veCostos } = usarUsuario();
  const [enCurso, setEnCurso] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const hoy = hoyEnHermosillo();
  const guardada = cot.borrador.parametros.promo ?? null;
  const puesta = guardada ? promoAlDia(guardada, hoy) : null;
  const vigente = promoVigente(hoy);
  const sinPromo = motivoSinPromo(cot.borrador);
  if (!puesta && (!puedeEditar || !vigente || sinPromo || opciones.some((o) => o.precio.precio_unico))) return null;
  const cambiar = async () => {
    setEnCurso(true);
    setError(null);
    try {
      // La promo del día en que se TOCA, no la del día en que se dibujó la pantalla: una pestaña abierta desde antes de la medianoche mandaba
      // la de ayer y el servidor contestaba «solo se pone con el botón» (verificación del 3-nov sobre 05e337e).
      const deHoy = promoDeHoy(hoyEnHermosillo());
      await ejecutar((c) => {
        const antes = c.borrador.parametros.promo ?? null;
        if (!antes && !deHoy) throw new Error('La promo ya terminó: recarga la página.');
        return api.editarBorrador(c.id, c.revision, [reemplazo('/parametros/promo', antes, antes ? null : deHoy)]);
      });
    } catch (e) {
      setError(e);
    } finally {
      setEnCurso(false);
    }
  };
  const conPrecio = opciones.filter((o) => o.precio.promo);
  // Diego, 3-nov-2026: la promo de casa trae también 18 MSI con tarjeta (sobre la lista, con el tope de siempre): el PDF y el WhatsApp los
  // ofrecen; aquí el vendedor ve la mensualidad de cada opción y, quien ve costos, la comisión del banco que conoce el cotizador.
  const esCasa = cot.borrador.cuestionario.tipo === 'casa';
  const msi18 = (o: OpcionCalculada) => (esCasa ? mensualidadMsiPromo(o.precio.lista) : null);
  // Los 18 MSI van por opción, con el tope de lista de siempre: el cierre del PDF, el WhatsApp y «Hoy toca» los dicen solo si la RECOMENDADA
  // los lleva, y aquí se dice en cuáles no van (re-verificación del 3-nov sobre e15b084: la caja los prometía en toda casa, aunque la lista
  // de la recomendada pasara de $500,000 y el PDF no los trajera).
  const rec = opciones.find((o) => o.id === recomendada);
  const conMsi18 = !!rec && msi18(rec) !== null ? ` o ${PROMO_MSI_MESES} meses sin intereses con tarjeta de crédito` : '';
  const sin18 = esCasa ? opciones.filter((o) => msi18(o) === null) : [];
  const con18 = esCasa ? opciones.filter((o) => msi18(o) !== null) : [];
  const nombres = (os: readonly OpcionCalculada[]) => os.map((o) => ETIQUETA_OPCION[o.id]).join(', ').replace(/, ([^,]*)$/, ' y $1');
  const tope = `$${numero(MSI_TOPE_LISTA_CON_IVA, 0)}`;
  const notaMsi18 = !sin18.length
    ? ''
    : !con18.length
      ? ` Sin ${PROMO_MSI_MESES} MSI: la lista de ${sin18.length === 1 ? 'la opción' : `las ${sin18.length} opciones`} pasa de ${tope}.`
      : ` Sin ${PROMO_MSI_MESES} MSI en ${nombres(sin18)}: su lista pasa de ${tope}${conMsi18 ? '' : `; en el PDF solo van en ${nombres(con18)}`}.`;
  // Pasado el 31-dic la promo no se quita sola (el folio no cambia solo): la pantalla ya no dice «Activa» (verificación del 2-nov, ronda 2).
  const vencida = !!puesta && hoy > puesta.vence;
  // Una promo que no es de la tabla (otro id u otro %: llegó sin el botón) no se cobra: la pantalla dice lo mismo que el rojo del motor (ronda 3).
  const ajena = guardada ? promoDesconocida(guardada) : null;
  return (
    <div className="aviso-ancho aviso-ancho--ambar pila" data-promo-boton data-promo-vencida={vencida || undefined} data-promo-ajena={ajena ? true : undefined}>
      <div className="fila">
        {puedeEditar && (
          <Boton variante={puesta ? 'secundario' : 'principal'} disabled={enCurso} onClick={cambiar}>
            {puesta ? `Quitar promo ${puesta.nombre}` : vigente?.etiqueta}
          </Boton>
        )}
        <p>
          {ajena
            ? `Esta promo no se reconoce (${ajena}): no se cobra y el PDF no sale mientras esté puesta. Quítala.`
            : vencida
            ? `La promo ${puesta!.nombre} venció el ${fechaLarga(puesta!.vence)}. El precio sigue con ella hasta que la quites, y el PDF no sale mientras tanto: quítala para cotizar al precio normal.`
            : puesta
              ? `${puedeEditar ? 'Activa' : `Promo ${puesta.nombre} activa`}: al cliente le dice «Promo ${puesta.nombre}: ${puesta.pct} % de descuento de contado${conMsi18}», hasta el ${fechaLarga(puesta.vence)}.${notaMsi18} Etapas, FINSUS y los MSI van sobre el precio de siempre.`
              : `Al cliente: «${vigente?.etiqueta}: ${PROMO_PCT} % de descuento de contado${conMsi18}», hasta el ${vigente ? fechaLarga(vigente.hasta) : ''}.${notaMsi18} Hoy el contado lleva ${DESCUENTO_CONTADO_PCT} %; etapas, FINSUS y los MSI no cambian.`}
        </p>
      </div>
      {conPrecio.map((o) => (
        <p key={o.id}>{ETIQUETA_OPCION[o.id]}: contado normal {dinero(o.precio.promo!.contado_normal)} · promo {dinero(o.precio.contado)}{msi18(o) !== null ? ` · ${PROMO_MSI_MESES} MSI de ${dinero(msi18(o), false)}` : ''}</p>
      ))}
      {conPrecio.some((o) => msi18(o) !== null) && veCostos && (
        <p data-promo-msi-comision>
          <strong>Comisión de los {PROMO_MSI_MESES} MSI:</strong> el banco cobró {numero(MSI_COSTO_BANCO_PCT, 2)} % en las últimas ventas a meses (plazo sin confirmar) y la lista solo cubre {DESCUENTO_CONTADO_PCT} %; a {PROMO_MSI_MESES} meses suele cobrar más. Lo decide Diego.
        </p>
      )}
      {conPrecio.length > 0 && (
        <p data-promo-efectivo>
          <strong>Ojo con el efectivo:</strong> el descuento de hasta {DESCUENTO_EFECTIVO_MAX_PCT} % se sumaría encima de la promo ({conPrecio.map((o) => `${ETIQUETA_OPCION[o.id]}: ${dinero(contadoEnEfectivo(o.precio.contado))}`).join(' · ')}). Avisa a Diego antes de ofrecerlo.
        </p>
      )}
      {error ? <ErrorEnLinea error={error} /> : null}
    </div>
  );
}

/** Semáforo de margen: el director ve color + % (dentro de su rentabilidad); los demás (internos, externos y Optimus: Diego,
 *  13-oct, junta), SOLO el color (`semaforo_margen`, sin % ni montos). Lo decide el SERVIDOR con lo que manda. */
function Semaforo({ o }: { o: ConMargen }) {
  const r = o.rentabilidad;
  const color = colorSemaforo(o);
  if (!color) return null;
  return (
    <span className={`semaforo semaforo--${color}`} data-semaforo={color}>
      <span className="semaforo__punto" aria-hidden />
      {ETIQUETA_SEMAFORO[color]}
      {r ? ` · ${numero(r.margen_contado_pct, 1)} % de margen` : ''}
    </span>
  );
}

function Desglose({ o }: { o: OpcionCalculada }) {
  const p = o.precio;
  // El IVA se enseña como total − subtotal (así cuadra al centavo con lo que manda el servidor al PDF).
  const iva = redondearCentavos(p.contado - p.contado_sin_iva);
  return (
    <details className="plegable plegable--interior">
      <summary>
        <ChevronDown aria-hidden className="plegable__flecha" />
        Cómo se arma el precio
      </summary>
      <table className="tabla tabla--compacta">
        <tbody>
          <tr>
            <th scope="row">
              Equipo e instalación ({kwp(o.kwp)}{p.contado_sin_iva_usd_por_w === undefined ? ` × $${numero(p.precio_lista_sin_iva_por_w, 2)}/W` : ''})
            </th>
            <td className="num">{dinero(p.equipo_e_instalacion_sin_iva, false)}</td>
          </tr>
          {p.fijos.map((f, i) => (
            <tr key={`f${i}`}>
              <th scope="row">
                {f.concepto}
                {!f.confirmado && <span className="tabla__marca tabla__marca--ambar">sin confirmar</span>}
              </th>
              <td className="num">{dinero(f.monto_sin_iva, false)}</td>
            </tr>
          ))}
          {p.adicionales.map((f, i) => (
            <tr key={`a${i}`}>
              <th scope="row">{f.concepto}</th>
              <td className="num">{dinero(f.monto_sin_iva, false)}</td>
            </tr>
          ))}
          {p.ajuste_manual && (
            <tr>
              <th scope="row">Ajuste a mano: {p.ajuste_manual.motivo}</th>
              <td className="num">{dinero(p.ajuste_manual.monto_sin_iva, false)}</td>
            </tr>
          )}
          {/* Precio único (Diego, 13-oct): sin lista ni descuento de contado, solo el subtotal del único precio */}
          {!p.precio_unico && (
            <tr className="es-total">
              <th scope="row">Lista sin IVA (con tarjeta)</th>
              <td className="num">{dinero(p.lista_sin_iva, false)}</td>
            </tr>
          )}
          {!p.precio_unico && (
            <tr data-descuento-contado>
              {/* con promo, el contado va al % de la promo; descuento_contado_pct sigue siendo el de la tabla (aplicarPromo) */}
              <th scope="row">Descuento de contado{p.promo ? ' con promo' : ''} ({numero(p.promo?.pct ?? p.descuento_contado_pct, Number.isInteger(p.promo?.pct ?? p.descuento_contado_pct) ? 0 : 1)} %)</th>
              <td className="num">−{dinero(redondearCentavos(p.lista_sin_iva - p.contado_sin_iva), false)}</td>
            </tr>
          )}
          <tr className="es-total">
            <th scope="row">{p.precio_unico ? 'Subtotal (precio único)' : 'Subtotal de contado'}</th>
            <td className="num">{dinero(p.contado_sin_iva, false)}</td>
          </tr>
          <tr>
            <th scope="row">IVA ({p.iva_pct} %)</th>
            <td className="num">{dinero(iva, false)}</td>
          </tr>
          <tr className="es-total">
            <th scope="row">Total de contado</th>
            <td className="num">{dinero(p.contado, false)}</td>
          </tr>
          {p.promo && (
            <tr data-promo>
              <th scope="row">Contado normal, sin promo</th>
              <td className="num">{dinero(p.promo.contado_normal, false)}</td>
            </tr>
          )}
          {/* Diego, 13-oct (pedido 6): en efectivo hasta 5 % del total CON IVA (el IVA no se quita). Solo en pantalla. */}
          <tr data-efectivo>
            <th scope="row">
              Si paga en efectivo (hasta {DESCUENTO_EFECTIVO_MAX_PCT} % del total con IVA)
              <br />
              <small className="nota-suave">No va en el PDF. El IVA no se quita.</small>
              {p.promo && (
                <>
                  <br />
                  <small data-promo-efectivo>Se sumaría encima de la promo: avisa a Diego antes de ofrecerlo.</small>
                </>
              )}
            </th>
            <td className="num">{dinero(contadoEnEfectivo(p.contado), false)}</td>
          </tr>
        </tbody>
      </table>
    </details>
  );
}

/** «1 noche», «1.5 noches», «2 noches». */
function textoNoches(n: number): string {
  return `${numero(n, n % 1 ? 1 : 0)} ${n === 1 ? 'noche' : 'noches'}`;
}

/** Aislado (Diego, 28-oct: «debería cotizarse todo junto»): cómo se arma el precio del SISTEMA, con las mismas cifras que cobra el
 *  PDF (el nivel de la opción: paneles + baterías e inversor aislado). Sin costos (esos van en la rentabilidad, solo Diego). */
function DesgloseAislado({ paneles, baterias, total }: { paneles: number; baterias: number; total: NonNullable<NivelRespaldoCalculado['precio']> }) {
  return (
    <details className="plegable plegable--interior">
      <summary>
        <ChevronDown aria-hidden className="plegable__flecha" />
        Cómo se arma el precio
      </summary>
      <table className="tabla tabla--compacta">
        <tbody>
          <tr>
            <th scope="row">Paneles, estructura e instalación (contado con IVA)</th>
            <td className="num">{dinero(paneles, false)}</td>
          </tr>
          <tr>
            <th scope="row">Baterías, inversor aislado (sin red) e instalación (contado con IVA)</th>
            <td className="num">{dinero(baterias, false)}</td>
          </tr>
          <tr className="es-total">
            <th scope="row">Subtotal de contado</th>
            <td className="num">{dinero(total.contado_sin_iva, false)}</td>
          </tr>
          <tr>
            <th scope="row">IVA ({total.iva_pct} %)</th>
            <td className="num">{dinero(redondearCentavos(total.contado - total.contado_sin_iva), false)}</td>
          </tr>
          <tr className="es-total">
            <th scope="row">Total de contado (el del PDF)</th>
            <td className="num">{dinero(total.contado, false)}</td>
          </tr>
          <tr>
            <th scope="row">Con tarjeta de crédito</th>
            <td className="num">{dinero(total.lista, false)}</td>
          </tr>
          <tr data-efectivo>
            <th scope="row">
              Si paga en efectivo (hasta {DESCUENTO_EFECTIVO_MAX_PCT} % del total con IVA)
              <br />
              <small className="nota-suave">No va en el PDF. El IVA no se quita.</small>
            </th>
            <td className="num">{dinero(contadoEnEfectivo(total.contado), false)}</td>
          </tr>
        </tbody>
      </table>
    </details>
  );
}

/** Costo, margen y utilidad: SOLO Diego (ADENDA A0b, 11-oct). El servidor no manda la rentabilidad a nadie más; si no
 *  viene, aquí no se enseña nada (la interfaz nunca la calcula). Diego, 13-oct (pedido 7): de un vistazo, margen después de
 *  comisión, utilidad, costo total y comisión(es), con el desglose ABIERTO; y lo que queda si paga en efectivo (pedido 6). */
function Rentabilidad({ o, titulo }: { o: ConMargen; titulo?: string }) {
  const r = o.rentabilidad;
  if (!r) return null;
  const comisiones = r.desglose_costo.filter((l) => /^Comisión/.test(l.concepto));
  return (
    <div className={`rentabilidad rentabilidad--${r.semaforo}`} aria-label="Rentabilidad (solo Diego)">
      {titulo && <p className="opcion__etiqueta">{titulo}</p>}
      <div className="rentabilidad__cabeza">
        <Semaforo o={o} />
        <span className="rentabilidad__utilidad">{r.utilidad_contado_sin_iva < 0 ? `Pierde ${dinero(-r.utilidad_contado_sin_iva)}` : `Deja ${dinero(r.utilidad_contado_sin_iva)}`}</span>
      </div>
      <dl className="opcion__datos" data-rentabilidad>
        <div>
          <dt>Margen después de comisión</dt>
          <dd>{numero(r.margen_contado_pct, 1)} %</dd>
        </div>
        <div>
          <dt>Utilidad</dt>
          <dd>{dinero(r.utilidad_contado_sin_iva)} sin IVA</dd>
        </div>
        <div>
          <dt>Costo total</dt>
          <dd>{dinero(r.costo_total_sin_iva)} sin IVA</dd>
        </div>
        <div className="opcion__dato--ancho">
          <dt>{comisiones.length > 1 ? 'Comisiones' : 'Comisión'}</dt>
          <dd>{comisiones.length ? comisiones.map((l) => `${l.concepto}: ${dinero(l.monto_sin_iva)}`).join(' · ') : 'Sin comisión'}</dd>
        </div>
        {r.efectivo && (
          <div className="opcion__dato--ancho" data-efectivo-diego>
            <dt>Si paga en efectivo ({dinero(r.efectivo.contado)} con IVA)</dt>
            <dd>
              Margen {numero(r.efectivo.margen_pct, 1)} % · {r.efectivo.utilidad_sin_iva < 0 ? `pierde ${dinero(-r.efectivo.utilidad_sin_iva)}` : `deja ${dinero(r.efectivo.utilidad_sin_iva)}`} sin IVA · descuento {dinero(r.efectivo.descuento)}
            </dd>
          </div>
        )}
      </dl>
      <ul className="rentabilidad__motivos">
        {r.motivos.map((m, i) => (
          <li key={i}>{m}</li>
        ))}
      </ul>
      <details className="plegable plegable--interior" open>
        <summary>
          <ChevronDown aria-hidden className="plegable__flecha" />
          Costo para Solarix: {dinero(r.costo_total_sin_iva)} sin IVA
        </summary>
        <table className="tabla tabla--compacta">
          <tbody>
            {r.desglose_costo.map((l, i) => (
              <tr key={i}>
                <th scope="row">
                  {l.concepto}
                  {!l.confirmado && <span className="tabla__marca tabla__marca--ambar">sin confirmar</span>}
                </th>
                <td className="num">{dinero(l.monto_sin_iva, false)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

/** Diego, 13-oct (junta): color con el que queda un contado CON IVA, con los precios límite que manda el motor (null = no los
 *  trae: motor de antes). */
function colorConPrecio(t: number, l: OpcionCalculada['limites_precio']): 'verde' | 'amarillo' | 'rojo' | null {
  if (!l) return null;
  if (l.verde !== null && t >= l.verde) return 'verde';
  if (l.amarillo !== null && t >= l.amarillo) return 'amarillo';
  return 'rojo';
}
/** Al múltiplo de $100 más cercano (el redondeo del contado del motor). */
const aCien = (n: number): number => Math.max(100, Math.round(n / 100) * 100);

function FormaAjuste({ o, ajuste, alCerrar, conBaterias = false, aislado = false }: { o: OpcionCalculada; ajuste: AjusteOpcion | null; alCerrar: (texto?: string | null) => void; conBaterias?: boolean; aislado?: boolean }) {
  const { mandar, error, setError, ocupado } = usarEditarParametros();
  const regenerar = usarRegenerarPdf();
  /** Tras guardar: si ya había PDF, la versión siguiente sale sola (PLAN-NOCHE §2b, criterio 3). */
  const despues = async (motivo: string) => {
    const r = await regenerar(motivo);
    alCerrar(r.tipo === 'regenerado' ? `Listo: se generó la versión ${r.numero} del PDF con este cambio.` : r.tipo === 'pendiente' ? `El PDF anterior ya no dice lo último y no se regeneró solo: ${r.motivo}` : null);
  };
  const { vista } = usarVistaPrecios();
  // Diego, 13-oct (junta: «no cuadra con IVA y sin IVA y se mueven mucho los números»): el vendedor escribe LO QUE VE EL
  // CLIENTE, el total de CONTADO CON IVA (con o sin precio único es el mismo número). El ajuste se GUARDA como siempre, en
  // lista sin IVA (listaDesdeContado: ida y vuelta exacta), así las cotizaciones de antes se leen igual.
  const unico = o.precio.precio_unico === true;
  const iva = o.precio.iva_pct;
  const descTabla = o.limites_precio?.descuento_contado_pct ?? (unico ? (vista?.descuento_contado_pct ?? DESCUENTO_CONTADO_PCT) : o.precio.descuento_contado_pct);
  const contadoTabla = o.limites_precio?.contado_tabla ?? null;
  const precioInicial = ajuste?.precio_lista_sin_iva ? String(o.precio.contado) : '';
  const [paneles, setPaneles] = useState(ajuste?.paneles ? String(ajuste.paneles) : '');
  const [precio, setPrecio] = useState(precioInicial);
  const [motivo, setMotivo] = useState(ajuste?.motivo ?? '');
  const [ahorro, setAhorro] = useState(ajuste?.ahorro_pct ? String(ajuste.ahorro_pct) : '');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const { cot, detalle } = usarCotizacion();
  const { usuario } = usarUsuario();
  // Diego, 13-oct: el servidor mide el descuento con el tope del VENDEDOR DE LA COTIZACIÓN (Diego, Miguel y Juan Carlos 10 %;
  // externos 5 %), no con el de quien la edita. Si es mía, el mío; si es de otro, el mayor de los dos (el aviso del motor, al
  // guardar, dice si pide OK).
  const esMia = cot.vendedor_id === usuario.id;
  const topeInternos = vista?.descuento_max_sin_aprobacion_internos_pct ?? DESCUENTO_MAX_SIN_APROBACION_POR_OMISION;
  const topeExternos = vista?.descuento_max_sin_aprobacion_externos_pct ?? DESCUENTO_MAX_SIN_APROBACION_POR_OMISION;
  const tope = esMia ? (vista?.descuento_max_sin_aprobacion_pct ?? DESCUENTO_MAX_SIN_APROBACION_POR_OMISION) : Math.max(topeInternos, topeExternos);
  const tope0 = numero(tope, Number.isInteger(tope) ? 0 : 1);
  const crudo = numeroDe(precio);
  const pedido = crudo !== null && Number.isFinite(crudo) && crudo > 0 ? aCien(crudo) : null;
  const base = contadoTabla ?? o.precio.contado;
  const descuentoPedido = pedido !== null && contadoTabla ? ((contadoTabla - pedido) / contadoTabla) * 100 : null;
  // ¿cambia el tamaño en este guardado? Entonces los precios límite (de los paneles de hoy) ya no sirven para el color.
  const nPan = numeroDe(paneles);
  const nAh = numeroDe(ahorro.trim().replace(/,(\d{1,2})$/, '.$1'));
  const cambiaTamano = (nPan !== null && nPan !== (ajuste?.paneles ?? null)) || (nAh !== null && nAh !== (ajuste?.ahorro_pct ?? null)) || (nPan === null && nAh === null && ((ajuste?.paneles ?? null) !== null || (ajuste?.ahorro_pct ?? null) !== null));
  const color = pedido !== null && !cambiaTamano ? colorConPrecio(pedido, o.limites_precio) : null;
  const mover = (pct: number) => setPrecio(String(aCien((pedido ?? o.precio.contado) + (base * pct) / 100)));
  const minDeslizador = aCien(base * 0.7);
  const maxDeslizador = aCien(base * 1.3);

  async function guardar() {
    const e: Record<string, string> = {};
    const n = nPan;
    // Si cambian los paneles (o el % de ahorro) y el precio NO se tocó, el precio a mano se SUELTA: vuelve al de la tabla del
    // tamaño nuevo (Diego, 13-oct). Si en el mismo guardado escribe precio, manda lo que escribió.
    const precioTocado = precio.trim() !== precioInicial;
    const p = cambiaTamano && !precioTocado ? null : pedido;
    const a = nAh;
    if (n !== null && (!Number.isInteger(n) || n < 1)) e.paneles = 'Número entero de paneles.';
    if (crudo !== null && pedido === null) e.precio = 'Precio no válido.';
    if (a !== null && (!Number.isFinite(a) || a < 1 || a > 100)) e.ahorro = 'Entre 1 y 100 %.';
    if (n !== null && a !== null) e.ahorro = 'Escribe los paneles o el % de ahorro, no los dos: con el % el cotizador busca los paneles.';
    setErrores(e);
    if (Object.keys(e).length) return;
    // «Precio de tabla» (o el mismo precio que la tabla) = sin precio a mano
    const listaGuardada = p !== null && !(contadoTabla !== null && p === contadoTabla && !cambiaTamano) ? listaDesdeContado(p, iva, descTabla) : null;
    if (n === null && listaGuardada === null && a === null) {
      if (ajuste) return void (await quitar());
      setErrores({ paneles: 'Cambia los paneles, el % de ahorro o el precio.' });
      return;
    }
    // Diego, 13-oct (junta, punto b): el motivo nunca es obligatorio; vacío, queda uno que dice qué se movió.
    const porOmision = [n !== null ? `${n} paneles` : a !== null ? `${numero(a, Number.isInteger(a) ? 0 : 1)} % de ahorro` : '', listaGuardada !== null && p !== null ? `contado ${dinero(p)} con IVA` : ''].filter(Boolean).join(', ');
    const textoMotivo = motivo.trim().length >= 3 ? motivo.trim() : `Ajuste a mano: ${porOmision}`.slice(0, 300);
    const nuevo: AjusteOpcion = { opcion: o.id, paneles: panelesDelAjuste(n, listaGuardada !== null, conBaterias, o.paneles), precio_lista_sin_iva: listaGuardada, motivo: textoMotivo };
    const ok = await mandar((c) => {
      const lista = c.borrador.parametros.ajustes;
      const i = lista.findIndex((x) => x.opcion === o.id);
      // Diego, 13-oct (pedido 2): con el % de ahorro escrito (null = sin %)
      if (i >= 0) return [reemplazo(`/parametros/ajustes/${i}`, lista[i], { ...nuevo, ahorro_pct: a }, nuevo.motivo)];
      return [{ op: 'agregar', ruta: '/parametros/ajustes/-', antes: null, despues: { ...nuevo, ahorro_pct: a }, motivo: nuevo.motivo }];
    });
    if (ok) await despues(`Ajuste de la opción ${ETIQUETA_OPCION[o.id].toLowerCase()}: ${nuevo.motivo}`);
  }
  async function quitar() {
    const ok = await mandar((c) => {
      const lista = c.borrador.parametros.ajustes;
      const i = lista.findIndex((a) => a.opcion === o.id);
      return [{ op: 'quitar', ruta: `/parametros/ajustes/${i}`, antes: lista[i], despues: null, motivo: 'Quitar ajuste a mano' }];
    });
    if (ok) await despues(`Quitó el ajuste de la opción ${ETIQUETA_OPCION[o.id].toLowerCase()}`);
  }
  const subtotal = pedido !== null ? redondearCentavos(pedido / (1 + iva / 100)) : null;

  return (
    <div className="ajuste pila">
      <p className="ajuste__titulo">Ajustar la opción {ETIQUETA_OPCION[o.id].toLowerCase()}</p>
      <div className="rejilla-2">
        <CampoTexto etiqueta="Paneles" ayuda={o.paneles_por_retorno ? `Hoy: ${o.paneles} (${o.mejor_retorno ? 'Mejor retorno' : 'los eligió el cotizador por su retorno'}). Si solo escribes el precio, se queda con estos ${o.paneles} paneles; con «${cot.borrador.parametros.verde_de_entrada === true ? 'Precio de entrada' : 'Precio de tabla'}» el cotizador los vuelve a buscar. Al cambiarlos, el precio vuelve al de la tabla.` : `Hoy: ${o.paneles}. Vacío = los que calcula. Al cambiarlos, el precio vuelve al de la tabla.`} inputMode="numeric" value={paneles} onChange={(e) => setPaneles(e.target.value)} error={errores.paneles} />
        {/* Diego, 13-oct (pedido 2): "a veces nos basamos en el % de ahorro con el cliente": el servidor busca los paneles */}
        {/* z33 (vuelta 2, M3): un aislado no tiene recibo de CFE: no hay % de ahorro que buscar */}
        {!aislado && <CampoTexto etiqueta="% de ahorro" ayuda={o.ahorro_pct !== undefined ? `Hoy: ${numero(o.ahorro_pct, 1)} %. Busca los paneles.` : 'Aparece al recalcular. Busca los paneles.'} inputMode="decimal" sufijo="%" value={ahorro} onChange={(e) => setAhorro(e.target.value)} error={errores.ahorro} />}
      </div>
      <CampoTexto
        etiqueta={unico ? 'Precio único de contado CON IVA' : 'Total de contado CON IVA'}
        ayuda={`Hoy: ${dinero(o.precio.contado)}${contadoTabla !== null ? ` · tabla: ${dinero(contadoTabla)}` : ''}. Lo que ve el cliente; se redondea a $100.`}
        inputMode="decimal"
        sufijo="$"
        placeholder={String(o.precio.contado)}
        value={precio}
        onChange={(e) => setPrecio(e.target.value)}
        error={errores.precio}
        data-campo="contado-con-iva"
      />
      {/* Diego, 13-oct (junta): precio LIBRE, arriba o abajo; fácil en el celular */}
      <div className="fila-botones" aria-label="Mover el precio">
        <button type="button" className="pastilla" onClick={() => mover(-1)} aria-label="Bajar 1 %">−1 %</button>
        <input
          type="range"
          aria-label="Precio de contado con IVA"
          min={minDeslizador}
          max={maxDeslizador}
          step={100}
          value={Math.min(maxDeslizador, Math.max(minDeslizador, pedido ?? o.precio.contado))}
          onChange={(e) => setPrecio(e.target.value)}
          style={{ flex: '1 1 8rem', minWidth: '6rem' }}
        />
        <button type="button" className="pastilla" onClick={() => mover(1)} aria-label="Subir 1 %">+1 %</button>
      </div>
      <div className="ideas" aria-label="Precios rápidos">
        {o.limites_precio?.verde != null && (
          // Verificación del 13-oct: con el tamaño cambiado en esta misma forma, el verde y la tabla son los del tamaño VIEJO;
          // se apagan hasta aplicar el tamaño nuevo (si no, se guardaba a mano el precio de 14 paneles para 20).
          <button type="button" className="pastilla" disabled={cambiaTamano} title={cambiaTamano ? 'Aplica primero los paneles o el % nuevo' : undefined} onClick={() => setPrecio(String(o.limites_precio!.verde))}>
            Llévalo a verde
          </button>
        )}
        {contadoTabla !== null && (
          <button type="button" className="pastilla" disabled={cambiaTamano} title={cambiaTamano ? 'Aplica primero los paneles o el % nuevo' : undefined} onClick={() => setPrecio(String(contadoTabla))}>
            {cot.borrador.parametros.verde_de_entrada === true ? 'Precio de entrada' : 'Precio de tabla'}
          </button>
        )}
      </div>
      {/* Verde de entrada (2-nov): solo a Diego le llega contado_sin_regla (vista.ts): cuánto daba la tabla sin la regla. */}
      {o.limites_precio?.contado_sin_regla !== undefined && o.limites_precio.contado_sin_regla !== contadoTabla && (
        <p className="nota-suave" data-sin-regla>{`Sin la regla de verde de entrada, la tabla daría ${dinero(o.limites_precio.contado_sin_regla)}.`}</p>
      )}
      {pedido !== null && subtotal !== null && (
        <p className="nota-suave" data-cuadre>
          {color && (
            <span className={`semaforo semaforo--${color}`} data-semaforo-vivo={color}>
              <span className="semaforo__punto" aria-hidden />
              {ETIQUETA_SEMAFORO[color]}
            </span>
          )}{' '}
          {`Queda en ${dinero(pedido)} con IVA = ${dinero(subtotal)} + IVA ${dinero(redondearCentavos(pedido - subtotal))}`}
          {!unico && descTabla > 0 ? ` · con tarjeta: ${dinero(redondearCentavos(pedido / (1 - descTabla / 100)))}` : ''}
          {cambiaTamano ? ' · cambiaste el tamaño: el color se ve al aplicar.' : ''}
        </p>
      )}
      {/* E6 (decisión 8 de Diego, 24-oct): con baterías por opción el vendedor mueve los PANELES y el color mide el TOTAL */}
      {conBaterias && <p className="nota-suave">Con baterías: escribes el precio de los paneles; el color y «Llévalo a verde» miden el total (paneles + baterías). Las baterías no cambian.</p>}
      <p className={['nota-suave', descuentoPedido !== null && descuentoPedido > tope + 0.05 ? 'texto-rojo' : ''].join(' ')}>
        {descuentoPedido !== null && descuentoPedido > tope + 0.05
          ? `Queda ${numero(descuentoPedido, 1)} % abajo de la tabla: pasa del ${tope0} % que ${esMia ? 'puedes' : 'se puede'} bajar sin OK. Se puede guardar; el aviso le llega a Diego, Miguel o Juan Carlos.`
          : descuentoPedido !== null && descuentoPedido < -0.05
            ? `Queda ${numero(-descuentoPedido, 1)} % arriba de la tabla.`
            : !esMia
              ? `Sube o baja libre. Esta cotización es de ${detalle.vendedor_nombre}: sin OK baja hasta ${numero(topeInternos, Number.isInteger(topeInternos) ? 0 : 1)} % (Diego, Miguel y Juan Carlos) o ${numero(topeExternos, Number.isInteger(topeExternos) ? 0 : 1)} % (externos) mientras siga en verde. Si sale en rojo, súbelo.`
              : `Sube o baja libre. Sin OK puedes bajar hasta ${tope0} % de la tabla mientras siga en verde. Si sale en rojo, súbelo.`}
      </p>
      <CampoArea etiqueta="Motivo" ayuda="Opcional: queda en el historial." rows={2} maxLength={300} placeholder="Ej. en el techo solo caben 14 paneles" value={motivo} onChange={(e) => setMotivo(e.target.value)} error={errores.motivo} />
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      <div className="fila-botones fila-botones--fin">
        {ajuste && (
          <Boton chico variante="fantasma" icono={<Trash2 aria-hidden />} onClick={() => void quitar()} disabled={ocupado}>
            Quitar ajuste
          </Boton>
        )}
        <Boton chico variante="fantasma" onClick={() => alCerrar(null)}>
          Cancelar
        </Boton>
        <Boton chico variante="noche" cargando={ocupado} onClick={() => void guardar()}>
          Aplicar ajuste
        </Boton>
      </div>
    </div>
  );
}

/** Porcentaje como lo escribe la leyenda de A0 (textoLeyendaFinsus): hasta 2 decimales, sin ceros de más. */
const formatoPct = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });
const pct = (n: number): string => formatoPct.format(n);

/** FINSUS de REFERENCIA de una opción (ADENDA A0): enganche, mensualidad a 60 meses y la tasa anual. Discreto: es una
 *  forma de pago más, no la cifra protagonista. El CAT NO va aquí (solo en la leyenda chica de abajo). */
function FinsusOpcion({ f }: { f: FinsusReferencia }) {
  return (
    <div className="opcion__finsus">
      <p className="opcion__finsus-etiqueta">Con FINSUS</p>
      <p className="opcion__finsus-cifras">
        Enganche {dinero(f.enganche)} ({f.enganche_pct} %) · desde <strong>{dinero(f.mensualidad)}</strong> al mes a {f.plazo_meses} meses
      </p>
      <p className="opcion__finsus-tasa">
        Tasa de interés {pct(f.tasa_anual_pct)} % anual{f.producto === 'empresarial' ? ' sin IVA en intereses (crédito empresarial de FINSUS)' : ''}
      </p>
      {typeof f.seguro_danos_contado === 'number' && typeof f.apertura_contado === 'number' ? (
        // Diego, 28-oct-2026: el seguro de daños y la apertura se pagan de contado, junto con el enganche
        <p className="opcion__finsus-tasa">
          + seguro y apertura {dinero(f.seguro_danos_contado + f.apertura_contado)} de contado (al inicio: {dinero(f.enganche + f.seguro_danos_contado + f.apertura_contado)})
        </p>
      ) : (
        typeof f.apertura_contado === 'number' && <p className="opcion__finsus-tasa">Más comisión por apertura de {dinero(f.apertura_contado)} de contado</p>
      )}
    </div>
  );
}

/** Leyenda chiquita de A0 (una por tasa distinta; en la práctica una sola). Sin "crédito bancario" ni "cotización FINSUS".
 *  Con FINSUS APAGADO lo dice en el mismo lugar y del mismo tamaño (nada de rojo): Diego pide la mensualidad de
 *  referencia en toda propuesta con precio (A0b), y sin esta línea el vendedor solo se enteraba al generar el PDF. */
function LeyendaFinsus({ r, finsusEncendido, puedeEditar }: { r: ResultadoMotor; finsusEncendido: boolean; puedeEditar: boolean }) {
  const { mandar, error, setError, ocupado } = usarEditarParametros();
  if (!finsusEncendido)
    return (
      <div className="leyenda-finsus leyenda-finsus--apagado">
        <p>FINSUS está apagado: estas opciones no llevan enganche ni mensualidad de referencia. Diego la pide en toda propuesta con precio.</p>
        {puedeEditar && (
          <button
            type="button"
            className="enlace-boton"
            disabled={ocupado}
            onClick={() => void mandar(() => [reemplazo('/parametros/formas_de_pago/finsus', false, true, 'Formas de pago')])}
          >
            {ocupado ? 'Encendiendo…' : 'Encender FINSUS'}
          </button>
        )}
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      </div>
    );
  const calculadas = r.opciones.map((o) => o.finsus_referencia).filter((f): f is FinsusReferencia => Boolean(f));
  // Motor de antes de la A0 (ninguna opción trae la clave) con FINSUS encendido: se ve al recalcular.
  if (finsusEncendido && r.opciones.every((o) => o.finsus_referencia === undefined))
    return <p className="leyenda-finsus">La mensualidad de referencia de FINSUS aparece al recalcular.</p>;
  if (!calculadas.length) return null;
  const textos = [...new Set(calculadas.map((f) => textoLeyendaFinsus(f)))];
  return (
    <>
      {textos.map((t) => (
        <p key={t} className="leyenda-finsus">
          {t}
        </p>
      ))}
    </>
  );
}

export function TarjetaOpcion({ o, r, parametros, puedeEditar, invExactaDisponible = false, aislado = false }: { o: OpcionCalculada; r: ResultadoMotor; parametros: ParametrosCalculo; puedeEditar: boolean; invExactaDisponible?: boolean; aislado?: boolean }) {
  const { mandar, error, setError, ocupado, ponerModulos, fijados } = usarModulosRespaldo(r, parametros);
  const [ajustando, setAjustando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const recomendada = r.recomendada === o.id;
  const ajuste = parametros.ajustes.find((a) => a.opcion === o.id) ?? null;
  const banda = BANDA_COBERTURA[o.id];
  // z16/z23: el «Mejor retorno» de casa 1F no tiene banda (sus paneles salen del menor retorno)
  // z33: el aislado no va por bandas de cobertura sino por noches (sus paneles dan el día y recargan su banco)
  const fueraDeBanda = !aislado && !o.mejor_retorno && (o.cobertura_real < banda.min || o.cobertura_real > banda.max);
  const nombreOpcion = o.mejor_retorno ? 'Mejor retorno' : ETIQUETA_OPCION[o.id];
  const p = o.precio;
  // ADENDA z31: con recibo bimestral, «ahorra $X al bimestre» (sin «promedio mensual»); mensual, como siempre.
  const ahorro = ahorroPorPeriodoImpreso(r.periodos, o.pago_con_sistema, r.facturacion);
  // E4b (Diego, 24-oct, decisiones 1 y 8): por opción, la tarjeta es el SISTEMA completo (paneles + baterías + híbrido); el
  // precio, el semáforo y la rentabilidad son del total; el retorno sigue siendo solo de los paneles (ADENDA 14-oct).
  const nivel = nivelDeOpcion(r, o.id);
  const sis = nivel?.sistema ?? null;
  const total = nivel?.precio ?? null;
  const bats = nivel ? (nivel.solo_respaldo?.precio.contado ?? (total && sis ? redondearCentavos(total.contado - sis.paneles_contado) : null)) : null;
  const hibrido = nivel?.inversor ?? null;
  const textoHibrido = hibrido ? `${hibrido.cantidad} × ${hibrido.modelo} · ${pct(hibrido.kw_continuos * hibrido.cantidad)} kW` : 'Falta en la tabla';
  // en CD los paneles entran directo al (a los) híbrido(s): el inversor de red de la opción sale
  const enCd = sis?.acople === 'cd';
  // z33 (Diego, 26-oct): en un AISLADO nunca se enseña el inversor de red de la opción (microinversor MIC/MIN…): solo el que
  // trabaja sin red, el de sus baterías; sin nivel todavía (cálculo de antes), se dice que falta.
  const inv =
    (enCd || aislado) && hibrido
      ? `${hibrido.cantidad > 1 ? `${hibrido.cantidad} × ` : ''}${hibrido.marca} ${hibrido.modelo}`
      : aislado
        ? 'inversor con baterías: recalcula la cotización'
        : o.inversores.map((x) => `${x.cantidad > 1 ? `${x.cantidad} × ` : ''}${x.marca} ${x.modelo}`).join(' + ');
  const margen: ConMargen = nivel ?? o;
  const color = colorSemaforo(margen);
  const casa = r.respaldo?.alcance !== 'cargas';
  const nombreBaterias = `Baterías ${o.mejor_retorno ? 'del Mejor retorno' : DE_LA[o.id]}`;

  return (
    <article className={['opcion', recomendada ? 'opcion--recomendada' : '', color ? `opcion--${color}` : ''].join(' ')} aria-label={`Opción ${nombreOpcion}`}>
      <header className="opcion__cabeza">
        <div>
          <h3 className="opcion__nombre">
            {nombreOpcion}
            {recomendada && (
              <span className="opcion__recomendada">
                <Star aria-hidden /> Recomendada
              </span>
            )}
          </h3>
          <p className={['opcion__cobertura', fueraDeBanda ? 'texto-ambar' : ''].join(' ')}>
            Cubre {porcentaje(o.cobertura_real)} del consumo
            {fueraDeBanda && ` · fuera de su banda habitual (${porcentaje(banda.min)}–${porcentaje(banda.max)}): la propuesta dice "${nombreOpcion} ${porcentaje(o.cobertura_real)} de tu consumo"`}
          </p>
          {o.mejor_retorno && <p className="opcion__cobertura">Menos paneles, cubre lo más caro del recibo: se paga más rápido.</p>}
        </div>
        {ajuste && <Chip tono="prusia">Ajustada a mano</Chip>}
      </header>

      <div className="opcion__precio">
        {nivel ? (
          <>
            <p className="opcion__etiqueta">Sistema completo, contado con IVA</p>
            <p className="opcion__cifra">{total ? dinero(total.contado) : 'Sin precio en la tabla'}</p>
            {total && (
              <p className="opcion__subtotal">
                {dinero(total.contado_sin_iva)} + IVA {dinero(redondearCentavos(total.contado - total.contado_sin_iva))}
              </p>
            )}
            <p className="opcion__subtotal" data-paneles-baterias>
              {`Paneles ${dinero(sis?.paneles_contado ?? p.contado)} + baterías${aislado ? ' e inversor aislado' : ''} ${bats === null ? 'sin precio en la tabla' : dinero(bats)}`}
            </p>
            {total && total.lista > total.contado && <p className="opcion__lista">{`Con tarjeta de crédito: ${dinero(total.lista)} con IVA`}</p>}
          </>
        ) : (
          <>
            {/* Diego, 28-oct: un aislado sin su nivel (cálculo de antes) NO tiene precio de sistema: esto es solo lo de los paneles */}
            <p className="opcion__etiqueta">{aislado ? 'Solo los paneles (falta recalcular), contado con IVA' : 'Contado con IVA'}</p>
            <p className="opcion__cifra">{dinero(p.contado)}</p>
            <p className="opcion__subtotal">
              {dinero(p.contado_sin_iva)} + IVA {dinero(redondearCentavos(p.contado - p.contado_sin_iva))}
            </p>
            {p.promo && parametros.promo && (hoyEnHermosillo() > promoAlDia(parametros.promo, hoyEnHermosillo()).vence ? <p className="opcion__subtotal" data-promo data-promo-vencida>Promo vencida: quítala · contado normal {dinero(p.promo.contado_normal)}</p> : <p className="opcion__subtotal" data-promo>Promo {promoAlDia(parametros.promo, hoyEnHermosillo()).nombre} · contado normal {dinero(p.promo.contado_normal)}</p>)}
            <p className="opcion__lista">
              {p.precio_unico
                ? // ADENDA z5 (14-oct) y z22 (16-oct): si UNA opción pasa del umbral, las TRES van con precio único (esta puede costar menos;
                  // el umbral se configura, así que no se escribe «$1 millón»)
                  'Precio único: si UNA opción pasa del umbral, las TRES van con precio único, sin lista ni descuento de contado'
                : // Diego, 13-oct (segunda tanda): el precio es el de CONTADO; la lista solo se usa si paga con tarjeta de crédito.
                  `Con tarjeta de crédito: ${dinero(p.lista)} con IVA`}
            </p>
          </>
        )}
        {p.contado_sin_iva_por_w !== undefined && (
          <p className="opcion__porw">
            {/* Diego, 11-oct: el precio por watt SIEMPRE en USD/W sin IVA (el MXN/W solo si la tabla vieja está en pesos) */}
            {nivel ? 'Paneles, contado sin IVA: ' : 'Contado sin IVA: '}<strong>{p.contado_sin_iva_usd_por_w !== undefined ? `${numero(p.contado_sin_iva_usd_por_w, 3)} USD/W` : `$${numero(p.contado_sin_iva_por_w, 2)} MXN/W`}</strong>
          </p>
        )}
        {/* z33: la competencia es de casas CON red (tarifa de CFE): no aplica a un aislado */}
        {!aislado && <CompetenciaOpcion tarifa={r.tarifa} kwp={o.kwp} usdPorW={p.contado_sin_iva_usd_por_w} />}
        {/* por opción, la mensualidad de referencia es la del sistema completo (la del nivel) */}
        {nivel ? nivel.finsus_referencia && <FinsusOpcion f={nivel.finsus_referencia} /> : o.finsus_referencia && <FinsusOpcion f={o.finsus_referencia} />}
      </div>

      <dl className="opcion__datos">
        <div>
          <dt>Sistema</dt>
          <dd>{kwp(o.kwp)}</dd>
        </div>
        <div>
          <dt>Paneles</dt>
          <dd>
            {o.paneles} × {numero(o.panel.w)} W
          </dd>
        </div>
        <div>
          <dt>Genera al año</dt>
          <dd>{kwh(o.generacion_kwh.reduce((a, b) => a + b, 0))}</dd>
        </div>
        {!enCd && !aislado && (
          <div className="opcion__dato--ancho">
            {/* Diego, 12-oct: el combo de inversores Growatt más barato que cumple el servicio (lo ven todos; sin costos) */}
            <dt>Inversores</dt>
            <dd>
              {o.inversores.map((x) => `${x.cantidad} × ${x.modelo}`).join(' + ')} · {numero(o.inversores.reduce((s, x) => s + x.kw * x.cantidad, 0), 1)} kW
            </dd>
          </div>
        )}
        {nivel && (
          <>
            <div className="opcion__dato--ancho">
              <dt>{aislado ? 'Inversor aislado (sin red)' : 'Inversor híbrido'}</dt>
              <dd>{textoHibrido}</dd>
            </div>
            <div className="opcion__dato--ancho">
              <dt>Baterías</dt>
              <dd>{`${nivel.bateria.modulos} × ${nivel.bateria.marca} ${nivel.bateria.modelo} · ${pct(nivel.bateria.kwh_utiles)} kWh útiles`}</dd>
            </div>
            {aislado && nivel.noches !== undefined ? (
              // E7/z33: el aislado va por NOCHES de autonomía (Arranque 1, Exacta 1.5, Holgada 2), no por horas
              <div className="opcion__dato--ancho">
                <dt>Noches de autonomía</dt>
                {/* Diego, 28-oct: con el +/− son las noches que aguantan de verdad esas baterías (noches_cubre); sin el dato
                    (cálculo de antes), las de la opción */}
                <dd>
                  {textoNoches(nivel.noches_cubre ?? nivel.noches)}
                  {nivel.noches_cubre !== undefined && Math.abs(nivel.noches_cubre - nivel.noches) > 0.05 ? ` con lo conectado (la opción pide ${textoNoches(nivel.noches)})` : ''}
                </dd>
              </div>
            ) : (
              <div className="opcion__dato--ancho">
                <dt>{casa ? 'Con toda la casa' : 'Con lo conectado'}</dt>
                <dd>{`De ${nivel.horas.min} a ${nivel.horas.max} h`}</dd>
              </div>
            )}
          </>
        )}
        {!aislado && (
        <div>
          <dt>Ahorro al año</dt>
          <dd>{dinero(o.ahorro_anual)}</dd>
        </div>
        )}
        {!aislado && o.ahorro_pct !== undefined && (
          // Diego, 13-oct (pedido 2): el % del recibo de hoy que se ahorra (el mismo con que se buscan los paneles)
          <div>
            <dt>Ahorro del recibo</dt>
            <dd>Ahorra {numero(o.ahorro_pct, 1)} % de tu recibo</dd>
          </div>
        )}
        {/* z33: un aislado no tiene recibo de CFE: ni ahorro ni retorno contra CFE */}
        {!aislado && (
        <div>
          <dt>{nivel ? 'Retorno de los paneles' : 'Retorno (de contado)'}</dt>
          <dd>{o.retorno_anios === null ? 'Sin ahorro' : `${numero(o.retorno_anios, 1)} años`}</dd>
        </div>
        )}
        {/* Diego, 28-oct (decisión): el aislado se compara contra un generador de diésel encendido todo el día, nunca contra CFE */}
        {aislado && o.generador && (
          <div>
            <dt>Con un generador de diésel</dt>
            <dd>{`Te costaría ${dinero(o.generador.costo_mes)} al mes`}</dd>
          </div>
        )}
        {aislado && o.generador && o.retorno_anios !== null && (
          <div>
            <dt>Contra el generador</dt>
            <dd>{`Se paga en ${numero(o.retorno_anios, 1)} años`}</dd>
          </div>
        )}
        {!aislado && o.fiscal && o.fiscal.retorno_anios !== null && (
          // Diego, 12-oct (ADENDA g, etapa 2): el segundo retorno es una cifra APARTE, junto al principal (que no se infla)
          <div>
            <dt>Con la deducción fiscal</dt>
            <dd>{`${numero(o.fiscal.retorno_anios, 1)} años`}</dd>
          </div>
        )}
      </dl>
      {!aislado && o.retorno_anios !== null && <p className="opcion__supuesto">{TEXTO_SUPUESTO_RETORNO}</p>}
      {aislado && o.generador && <p className="opcion__supuesto">{`Generador encendido todo el día: diésel a ${dinero(o.generador.precio_litro)} el litro, ${numero(o.generador.litros_por_kwh, 2)} litros por kWh; solo combustible. El diésel sube como la luz.`}</p>}
      {aislado && (
        <p className="opcion__supuesto">
          {nivel
            ? `Sin red de CFE: los paneles dan el día y recargan las baterías, que aguantan ${nivel.noches === undefined ? 'la noche' : textoNoches(nivel.noches_cubre ?? nivel.noches)}. El precio es el sistema completo (paneles + inversor aislado + baterías), el mismo del PDF.`
            : 'Aislado sin baterías todavía: recalcula la cotización para que salgan sus baterías, su inversor y su precio.'}
        </p>
      )}
      {!aislado && nivel && (
        <p className="opcion__supuesto">
          El retorno es solo de los paneles: las baterías son seguridad, no ahorro.
          {enCd && sis?.inversor_red ? ` Los paneles entran directo al inversor híbrido (sale ${sis.inversor_red}).` : ''}
        </p>
      )}
      {!aislado && (o.ahorro_demanda ?? 0) > 0 && (
        // Diego, 12-oct noche (punto a): en GDMTO/GDMTH el ahorro incluye capacidad y distribución con la fórmula de CFE
        // (medición neta, año típico). El vendedor ve de dónde sale; el PDF lo dice con «Estimado con las reglas de CFE».
        <p className="opcion__supuesto">
          Año típico con medición neta: {dinero(o.ahorro_anual - o.ahorro_demanda!)} de energía y {dinero(o.ahorro_demanda!)} de capacidad y distribución
          (fórmula de CFE). El primer año, sin saldo a favor, puede ahorrar menos.
        </p>
      )}
      {o.fiscal && (
        // Diego, 12-oct: incentivo fiscal (persona moral o actividad empresarial), aparte del retorno principal. Verificación
        // adversarial de la etapa 2: el segundo retorno ya va en su dato ("Con la deducción fiscal"); aquí solo el beneficio,
        // con las mismas palabras y la misma tasa que el PDF ("ISR de 35.5 %"), sin repetir el retorno ni "cuenta sin IVA".
        <p className="opcion__equipo">
          Beneficio fiscal estimado <strong>{dinero(o.fiscal.beneficio)}</strong> (ISR de {tasaIsr(o.fiscal.tasa_isr_pct)} %).
          <small> {TEXTO_LEYENDA_FISCAL}</small>
        </p>
      )}
      <p className="opcion__equipo">
        {o.panel.marca} {o.panel.modelo} · {inv}
        {!aislado && o.queda_en_minimo ? ' · el recibo queda en el mínimo' : ''}
        {!aislado && ahorro.cifra > 0 ? <> · ahorra {dinero(ahorro.cifra)} {ahorro.etiqueta}{ahorro.nota ? <> <small>{ahorro.nota}</small></> : null}</> : ''}
      </p>

      {margen.rentabilidad ? <Rentabilidad o={margen} titulo={nivel ? 'Margen del sistema completo (paneles + baterías)' : undefined} /> : margen.semaforo_margen ? <div className="rentabilidad__cabeza"><Semaforo o={margen} /></div> : null}
      {/* Diego ve además cuánto deja el respaldo solo (el servidor no se lo manda a nadie más) */}
      {nivel?.solo_respaldo?.rentabilidad && <RentabilidadRespaldo n={nivel} />}
      {nivel && total === null && <p className="nota-suave">Sin precio de las baterías o del híbrido en la tabla: el total y su semáforo no se pueden medir.</p>}
      {/* Diego, 28-oct: en el aislado el desglose es del SISTEMA (el de los paneles solos terminaba en otro «Total de contado») */}
      {aislado && nivel && total && sis && bats !== null ? <DesgloseAislado paneles={sis.paneles_contado} baterias={bats} total={total} /> : <Desglose o={o} />}
      {nivel && puedeEditar && (
        // Diego, 24-oct (decisión 1): +/− de baterías POR OPCIÓN; el servidor recalcula kWh, horas, precio y semáforo del total.
        // E5b (25-oct): cada híbrido lleva al menos un módulo: el «−» se apaga en la cantidad de híbridos de esa opción.
        <div className="respaldo-opciones__baterias" role="group" aria-label={`Baterías de la opción ${nombreOpcion}`}>
          <Boton chico variante="fantasma" icono={<Minus aria-hidden />} aria-label="Una batería menos" disabled={ocupado || nivel.bateria.modulos <= Math.max(1, nivel.inversor?.cantidad ?? 1)} onClick={() => ponerModulos(nivel.id, nivel.bateria.modulos - 1, `${nombreBaterias}: ${nivel.bateria.modulos - 1}`)} />
          <span>
            {nivel.bateria.modulos} {nivel.bateria.modulos === 1 ? 'batería' : 'baterías'}
          </span>
          <Boton chico variante="fantasma" icono={<Plus aria-hidden />} aria-label="Una batería más" disabled={ocupado || nivel.bateria.modulos >= 64} onClick={() => ponerModulos(nivel.id, nivel.bateria.modulos + 1, `${nombreBaterias}: ${nivel.bateria.modulos + 1}`)} />
          {fijados?.[nivel.id] != null && (
            <Boton chico variante="fantasma" icono={<RefreshCw aria-hidden />} disabled={ocupado} onClick={() => ponerModulos(nivel.id, null, `${nombreBaterias}: las que calcula el cotizador`)}>
              Automáticas
            </Boton>
          )}
        </div>
      )}
      {nivel && puedeEditar && !aislado && <HorasDeOpcion nivel={nivel} parametros={parametros} mandar={mandar} ocupado={ocupado} nombre={nombreBaterias} />}

      {invExactaDisponible && o.id === 'arranque' && (
        // Diego, 20-oct (ADENDA z24): «que ya compren el sistema con el inversor sobrado y listo para aumentar de paneles…
        // déjalo como opción para el que esté cotizando». Desde el 26-oct (Diego) las cotizaciones nuevas nacen PRENDIDAS; las
        // de antes (sin el campo) siguen apagadas. Se guarda en la cotización (bitácora).
        <Interruptor
          etiqueta="Inversor de la Exacta (listo para ampliar)"
          detalle={
            o.inversor_listo_para_ampliar
              ? o.inversor_listo_para_ampliar.mismo_inversor
                ? `Ya lleva el mismo inversor que la Exacta: listo para ampliar hasta ${numero(o.inversor_listo_para_ampliar.paneles_hasta)} paneles en total. El precio no cambia.`
                : o.inversor_listo_para_ampliar.precio_escrito
                  ? `Lleva el inversor de la Exacta: listo para ampliar hasta ${numero(o.inversor_listo_para_ampliar.paneles_hasta)} paneles en total. Tu precio escrito se queda igual: no incluye la diferencia del inversor.`
                  : `Lleva el inversor de la Exacta: listo para ampliar hasta ${numero(o.inversor_listo_para_ampliar.paneles_hasta)} paneles en total. El precio sube solo por la diferencia del inversor.`
              : parametros.inversor_de_exacta === true
                ? 'Prendido, pero con estos paneles no se pudo: revisa el aviso de esta opción.'
                : 'Apagado: lleva el inversor justo para sus paneles.'
          }
          marcado={parametros.inversor_de_exacta === true}
          disabled={!puedeEditar || ocupado}
          alCambiar={(v) => void mandar((c) => [reemplazo('/parametros/inversor_de_exacta', c.borrador.parametros.inversor_de_exacta, v, v ? 'Primera opción con el inversor de la Exacta (listo para ampliar)' : 'Primera opción con su inversor')])}
        />
      )}
      {puedeEditar && !ajustando && (
        <div className="opcion__acciones">
          {!recomendada && (
            <Boton chico variante="fantasma" icono={<Star aria-hidden />} cargando={ocupado} onClick={() => void mandar((c) => [reemplazo('/parametros/recomendada', c.borrador.parametros.recomendada, o.id, 'Cambio de recomendada')])}>
              Hacer recomendada
            </Boton>
          )}
          <Boton
            chico
            variante="fantasma"
            icono={<Pencil aria-hidden />}
            onClick={() => {
              setAviso(null);
              setAjustando(true);
            }}
          >
            {ajuste ? 'Cambiar ajuste' : 'Mueve el precio, los paneles o el % de ahorro'}
          </Boton>
        </div>
      )}
      {ajustando && (
        <FormaAjuste
          aislado={aislado}
          conBaterias={!!nivel?.precio}
          o={o}
          ajuste={ajuste}
          alCerrar={(texto) => {
            setAjustando(false);
            setAviso(texto ?? null);
          }}
        />
      )}
      {aviso && (
        <p className="nota-suave" role="status">
          {aviso}
        </p>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </article>
  );
}

/** Consumo contra generación por periodo (barras; sin horas ni "en vivo": es un cálculo). */
function Grafica({ r, opcion }: { r: ResultadoMotor; opcion: OpcionCalculada }) {
  const max = Math.max(1, ...r.periodos.map((p) => p.consumo_kwh), ...opcion.generacion_kwh);
  return (
    <figure className="grafica">
      <figcaption className="grafica__titulo">
        Consumo y generación por {r.facturacion === 'bimestral' ? 'bimestre' : 'mes'} · opción {ETIQUETA_OPCION[opcion.id].toLowerCase()}
        <span className="grafica__leyenda">
          <span className="grafica__clave grafica__clave--consumo" /> consumo
          <span className="grafica__clave grafica__clave--generacion" /> generación
        </span>
      </figcaption>
      <div className="grafica__barras" role="img" aria-label={`Consumo anual ${kwh(r.periodos.reduce((s, p) => s + p.consumo_kwh, 0))}; generación ${kwh(opcion.generacion_kwh.reduce((s, x) => s + x, 0))}`}>
        {r.periodos.map((p, i) => (
          <div key={i} className="grafica__grupo" title={`${p.etiqueta}: consumo ${kwh(p.consumo_kwh)}, generación ${kwh(opcion.generacion_kwh[i] ?? 0)}`}>
            <div className="grafica__par">
              <span className="grafica__barra grafica__barra--consumo" style={{ height: `${(p.consumo_kwh / max) * 100}%` }} />
              <span className="grafica__barra grafica__barra--generacion" style={{ height: `${((opcion.generacion_kwh[i] ?? 0) / max) * 100}%` }} />
            </div>
            <span className="grafica__etiqueta">{p.etiqueta.replace(/\s\d{2}$/, '')}</span>
          </div>
        ))}
      </div>
    </figure>
  );
}

/** Diego, 12-oct: "poder modificar los porcentajes de las 3 propuestas (la Holgada a 130 %…) y mover la cantidad de
 *  paneles". El vendedor escribe la cobertura (20–200 %) o los paneles de la opción y el servidor recalcula todo. Fuera de
 *  la banda habitual es aviso (la propuesta dice "<Opción> X % de tu consumo"), no error. */
const CON_ARTICULO: Record<OpcionId, string> = { arranque: 'el Arranque', exacta: 'la Exacta', holgada: 'la Holgada' };
const DE_LA: Record<OpcionId, string> = { arranque: 'del Arranque', exacta: 'de la Exacta', holgada: 'de la Holgada' };

function CoberturaOpcion({ id, parametros, r }: { id: OpcionId; parametros: ParametrosCalculo; r: ResultadoMotor }) {
  const { mandar, error, setError, ocupado } = usarEditarParametros();
  const regenerar = usarRegenerarPdf();
  const objetivo = parametros.coberturas_objetivo[id];
  const ajuste = parametros.ajustes.find((a) => a.opcion === id) ?? null;
  const o = r.opciones.find((x) => x.id === id) ?? null;
  const nombre = ETIQUETA_OPCION[id];
  const [pct, setPct] = useState(String(Math.round(objetivo * 1000) / 10));
  const [pan, setPan] = useState(ajuste?.paneles ? String(ajuste.paneles) : '');
  const [aviso, setAviso] = useState<string | null>(null);
  const [mal, setMal] = useState<{ pct?: string; pan?: string }>({});
  useEffect(() => setPct(String(Math.round(objetivo * 1000) / 10)), [objetivo]);
  useEffect(() => setPan(ajuste?.paneles ? String(ajuste.paneles) : ''), [ajuste?.paneles]);
  const despues = async (motivo: string) => {
    const x = await regenerar(motivo);
    setAviso(x.tipo === 'regenerado' ? `Listo: se generó la versión ${x.numero} del PDF con este cambio.` : x.tipo === 'pendiente' ? `El PDF anterior ya no dice lo último y no se regeneró solo: ${x.motivo}` : null);
  };
  async function aplicarPct() {
    const n = numeroDe(pct);
    if (n === null || !Number.isFinite(n) || n < 20 || n > 200) return setMal({ pct: 'Entre 20 y 200 %.' });
    // De menor a mayor entre las opciones activas (la más grande siempre lleva más paneles).
    const orden = OPCIONES.filter((x) => parametros.opciones_activas.includes(x) || x === id);
    const k = orden.indexOf(id);
    const antes = orden[k - 1];
    const sig = orden[k + 1];
    if (antes && n / 100 <= parametros.coberturas_objetivo[antes]) return setMal({ pct: `Más que ${CON_ARTICULO[antes]} (${porcentaje(parametros.coberturas_objetivo[antes])}).` });
    if (sig && n / 100 >= parametros.coberturas_objetivo[sig]) return setMal({ pct: `Menos que ${CON_ARTICULO[sig]} (${porcentaje(parametros.coberturas_objetivo[sig])}).` });
    setMal({});
    const motivo = `Cobertura ${DE_LA[id]}: ${numero(n, 1)} %`;
    const ok = await mandar((c) => {
      const ed: EdicionCampo[] = [reemplazo(`/parametros/coberturas_objetivo/${id}`, c.borrador.parametros.coberturas_objetivo[id], Math.round(n * 10) / 1000, motivo)];
      // Escribir la cobertura suelta los paneles puestos a mano en esa opción y también el precio a mano: cambia el tamaño, y el
      // precio vuelve al de la tabla del tamaño nuevo (Diego, 13-oct, junta: «se mueven mucho los números»).
      const lista = c.borrador.parametros.ajustes;
      const i = lista.findIndex((a) => a.opcion === id);
      const aj = lista[i];
      if (aj) ed.push({ op: 'quitar', ruta: `/parametros/ajustes/${i}`, antes: aj, despues: null, motivo });
      return ed;
    });
    if (ok) await despues(motivo);
  }
  async function aplicarPaneles() {
    const n = numeroDe(pan);
    if (n === null || !Number.isInteger(n) || n < 1) return setMal({ pan: 'Número entero de paneles.' });
    setMal({});
    const motivo = `${n} paneles en ${CON_ARTICULO[id]} (escritos en Ajustes del cálculo)`;
    const ok = await mandar((c) => {
      const lista = c.borrador.parametros.ajustes;
      const i = lista.findIndex((a) => a.opcion === id);
      // con otro tamaño el precio a mano se suelta (vuelve al de la tabla del tamaño nuevo; Diego, 13-oct, junta)
      if (i >= 0) return [reemplazo(`/parametros/ajustes/${i}`, lista[i], { ...lista[i]!, paneles: n, ahorro_pct: null, precio_lista_sin_iva: lista[i]!.paneles === n ? lista[i]!.precio_lista_sin_iva : null, motivo }, motivo)];
      const nuevo: AjusteOpcion = { opcion: id, paneles: n, precio_lista_sin_iva: null, motivo };
      return [{ op: 'agregar', ruta: '/parametros/ajustes/-', antes: null, despues: nuevo, motivo }];
    });
    if (ok) await despues(motivo);
  }
  const alEnter = (f: () => Promise<unknown>) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void f();
    }
  };
  return (
    <div className="pila pila--chica" aria-label={`Cobertura y paneles ${DE_LA[id]}`}>
      <div className="rejilla-2">
        <div className="pila pila--chica">
          <CampoTexto
            etiqueta={`Cobertura ${nombre}`}
            sufijo="%"
            inputMode="decimal"
            value={pct}
            disabled={ocupado}
            onChange={(e) => setPct(e.target.value)}
            onKeyDown={alEnter(aplicarPct)}
            error={mal.pct}
            ayuda={o ? `Hoy cubre ${porcentaje(o.cobertura_real)} con ${o.paneles} paneles.` : 'Escribe el % y aplica.'}
          />
          <Boton chico variante="fantasma" cargando={ocupado} onClick={() => void aplicarPct()}>
            Aplicar %
          </Boton>
        </div>
        <div className="pila pila--chica">
          <CampoTexto
            etiqueta={`Paneles ${nombre}`}
            inputMode="numeric"
            value={pan}
            disabled={ocupado}
            placeholder={o ? String(o.paneles) : ''}
            onChange={(e) => setPan(e.target.value)}
            onKeyDown={alEnter(aplicarPaneles)}
            error={mal.pan}
            ayuda={ajuste?.paneles ? 'Puestos a mano (queda en el historial).' : ajuste?.ahorro_pct ? `Buscados para ahorrar ${numero(ajuste.ahorro_pct, Number.isInteger(ajuste.ahorro_pct) ? 0 : 1)} % del recibo.` : 'Vacío = los que da el %.'}
          />
          <Boton chico variante="fantasma" cargando={ocupado} onClick={() => void aplicarPaneles()}>
            Aplicar paneles
          </Boton>
        </div>
      </div>
      {aviso && <p className="nota-suave">{aviso}</p>}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </div>
  );
}

function Parametros({ parametros, tipo, fiscal, r }: { parametros: ParametrosCalculo; tipo: string; fiscal: FiscalCuestionario | null; r: ResultadoMotor }) {
  const { mandar, error, setError, ocupado } = usarEditarParametros();
  // E4a (Diego, 24-oct): en SOLO RESPALDO no se venden paneles: sin coberturas, paneles por opción ni el panel de la tabla
  const soloRespaldo = r.respaldo?.tipo === 'respaldo';
  const fp = parametros.formas_de_pago;
  const negocio = tipo === 'comercio' || tipo === 'industria';
  const cambiar = (ruta: string, antes: unknown, despues: unknown, motivo: string) => void mandar(() => [reemplazo(ruta, antes, despues, motivo)]);
  return (
    <details className="plegable plegable--seccion">
      <summary>
        <ChevronDown aria-hidden className="plegable__flecha" />
        Ajustes del cálculo y formas de pago
      </summary>
      <div className="pila">
        <div className={soloRespaldo ? 'pila' : 'rejilla-2'}>
          {!soloRespaldo && (
            <div className="pila pila--chica">
              <p className="campo__etiqueta">Opciones en el PDF</p>
              {OPCIONES.map((id) => {
                // z16/z23 (20-oct): con la bandera puesta, la primera opción es «Mejor retorno» (sin cobertura objetivo: sus
                // paneles salen del menor retorno); decir «Arranque · Cobertura objetivo 60 %» aquí sería falso.
                const mr = r.opciones.find((o) => o.id === id && o.mejor_retorno === true) ?? null;
                return (
                <Interruptor
                  key={id}
                  etiqueta={mr ? 'Mejor retorno' : ETIQUETA_OPCION[id]}
                  detalle={mr ? `Menor retorno: ${mr.paneles} paneles, cubre ${porcentaje(mr.cobertura_real)}` : `Cobertura objetivo ${porcentaje(parametros.coberturas_objetivo[id])}`}
                  marcado={parametros.opciones_activas.includes(id)}
                  disabled={ocupado || (parametros.opciones_activas.length === 1 && parametros.opciones_activas.includes(id)) || parametros.recomendada === id}
                  alCambiar={(v) => {
                    const nuevas = v ? OPCIONES.filter((x) => x === id || parametros.opciones_activas.includes(x)) : parametros.opciones_activas.filter((x) => x !== id);
                    cambiar('/parametros/opciones_activas', parametros.opciones_activas, nuevas, `${v ? 'Agregar' : 'Quitar'} opción ${id}`);
                  }}
                />
                );
              })}
              <p className="nota-suave">Escribe la cobertura de cada opción (p. ej. Holgada 130 %) o sus paneles y se recalcula todo. Fuera de su banda habitual la propuesta dice, p. ej., "Holgada 130 % de tu consumo".</p>
              {OPCIONES.filter((id) => parametros.opciones_activas.includes(id)).map((id) => (
                <CoberturaOpcion key={id} id={id} parametros={parametros} r={r} />
              ))}
            </div>
          )}
          <div className="pila pila--chica">
            <p className="campo__etiqueta">Formas de pago</p>
            <Interruptor etiqueta="Pago en etapas" detalle="60/20/20 y 50 % + 10 % al mes" marcado={fp.etapas} disabled={ocupado} alCambiar={(v) => cambiar('/parametros/formas_de_pago/etapas', fp.etapas, v, 'Formas de pago')} />
            <Interruptor
              etiqueta="Meses sin intereses"
              detalle="3, 6 y 12 sobre precio de lista, con todas las tarjetas de crédito"
              marcado={fp.msi.length > 0}
              disabled={ocupado}
              alCambiar={(v) => cambiar('/parametros/formas_de_pago/msi', fp.msi, v ? [3, 6, 12] : [], 'Formas de pago')}
            />
            <Interruptor
              etiqueta="FINSUS"
              detalle={
                fp.finsus
                  ? `Enganche y mensualidad de referencia a ${FINSUS_REFERENCIA.plazo_meses} meses`
                  : 'Apagado: el PDF no sale así. Diego pide la mensualidad de referencia en toda propuesta con precio.'
              }
              marcado={fp.finsus}
              disabled={ocupado}
              alCambiar={(v) => cambiar('/parametros/formas_de_pago/finsus', fp.finsus, v, 'Formas de pago')}
            />
            {negocio && (
              <Interruptor
                etiqueta="Arrendamiento (ABC Leasing)"
                // Hallazgo del 10-oct: encendido, el PDF no sale (el cotizador todavía no captura la cotización de ABC
                // Leasing). Solo se deja APAGAR uno que venga encendido de antes.
                detalle={fp.arrendamiento ? 'Encendido, el PDF no sale: apágalo. La cotización de ABC Leasing la captura Diego aparte.' : 'Apagado, la propuesta de negocio muestra la renta DE REFERENCIA de ABC Leasing a 48 meses sin pago inicial (sujeta a su aprobación); la cotización formal la pide Diego.'}
                marcado={fp.arrendamiento}
                disabled={ocupado || !fp.arrendamiento}
                alCambiar={(v) => cambiar('/parametros/formas_de_pago/arrendamiento', fp.arrendamiento, v, 'Formas de pago')}
              />
            )}
            {negocio &&
              (fiscal && regimenNoDeduce(fiscal.regimen) ? (
                // Verificación adversarial de la etapa 2: particular o RESICO no deducen (LISR art. 113-E); el interruptor no
                // hacía nada aunque se viera encendido. Se enseña apagado y sin tocar, con el porqué.
                <Interruptor etiqueta="Deducción de impuestos" detalle={`No aplica: el cuestionario dice «${ETIQUETA_REGIMEN_FISCAL[fiscal.regimen]}». Si el cliente sí deduce, corrige su régimen fiscal en el cuestionario.`} marcado={false} disabled alCambiar={() => undefined} />
              ) : (
                <Interruptor etiqueta="Deducción de impuestos" marcado={fp.deduccion} disabled={ocupado} alCambiar={(v) => cambiar('/parametros/formas_de_pago/deduccion', fp.deduccion, v, 'Formas de pago')} />
              ))}
          </div>
        </div>
        <div className="rejilla-3">
          <CampoSelect
            etiqueta="IVA"
            value={String(parametros.iva_pct)}
            disabled={ocupado}
            onChange={(e) => cambiar('/parametros/iva_pct', parametros.iva_pct, Number(e.target.value), 'IVA')}
            // Adenda A0b (Diego, 10-oct-2026): IVA SIEMPRE 16 %, también en la frontera; el PDF no sale con otro. Un borrador
            // viejo con 8 % lo enseña para poder regresarlo a 16.
            opciones={[{ valor: '16', texto: '16 % (siempre)' }, ...(parametros.iva_pct === 8 ? [{ valor: '8', texto: '8 % (ya no se usa: cámbialo a 16)' }] : [])]}
          />
          {!soloRespaldo && <CampoTexto etiqueta="Panel" value={`${parametros.panel.marca} ${parametros.panel.modelo}`} disabled ayuda="Sale de la tabla de precios (Diego lo cambia en Configuración)." />}
          {!soloRespaldo && <CampoTexto etiqueta="Watts por panel" value={String(parametros.panel.w)} disabled />}
          {/* ADENDA z53-4 (Diego, 28-oct-2026): Growatt por omisión; cualquier vendedor puede pedir otra marca de las listas. El motor
              pone la combinación más barata de esa marca y el precio sube solo por la diferencia (la regla de extras de z24); el
              semáforo se mide igual. El aislado no lleva inversor de red (sus paneles van al inversor de las baterías). Con UNA sola
              marca (hoy: Solis espera la D1) no se enseña: nadie guarda la marca. */}
          {MARCAS_INVERSOR_RED.length > 1 && !soloRespaldo && tipo !== 'aislado' && r.opciones.some((o) => o.inversores.length > 0) && (
            <CampoSelect
              etiqueta="Marca de los inversores de red"
              value={parametros.marca_inversor_red ?? ''}
              disabled={ocupado}
              onChange={(e) => cambiar('/parametros/marca_inversor_red', parametros.marca_inversor_red ?? null, e.target.value || null, 'Marca de los inversores de red')}
              opciones={MARCAS_INVERSOR_RED.map((m, i) => (i === 0 ? { valor: '', texto: `${m} (por omisión)` } : { valor: m, texto: `${m} (el precio sube la diferencia)` }))}
              ayuda="Por omisión van Growatt. Con otra marca va la combinación más barata de esa marca y el precio sube solo lo que cuesta de más; si cuesta menos, no baja."
            />
          )}
        </div>
        {r.respaldo && !r.respaldo.pares?.length && <EquipoRespaldo parametros={parametros} r={r} cambiar={cambiar} ocupado={ocupado} />}
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      </div>
    </details>
  );
}

/** Baterías (Diego, 13-oct): el vendedor elige la batería y el inversor híbrido de la tabla (p. ej. Pylontech + LuxPower);
 *  vacío = el par compatible más barato para el cliente (Diego, 13-oct 23:00, respuesta u; el criterio exacto, POR CONFIRMAR
 *  CON DIEGO, y lo que costaría cada opción con la otra marca van en los supuestos del motor: verificación adversarial g0d-r2).
 *  El motor avisa si el par no es compatible o si un id ya no está en la tabla. */
function EquipoRespaldo({ parametros, r, cambiar, ocupado }: { parametros: ParametrosCalculo; r: ResultadoMotor; cambiar: (ruta: string, antes: unknown, despues: unknown, motivo: string) => void; ocupado: boolean }) {
  const { vista } = usarVistaPrecios();
  const eq = parametros.equipo_respaldo ?? null;
  const bats = vista?.equipos_respaldo?.baterias ?? [];
  const invs = vista?.equipos_respaldo?.inversores ?? [];
  const n = r.respaldo?.niveles[0] ?? null;
  // Con la otra marca las opciones pueden ser otras (p. ej. una sola Fidus Plus alcanza 2, 4 y 8 h): el vendedor sabe dónde verlo.
  const porQue = !eq && r.supuestos.some((s) => s.includes('(par por omisión)')) ? ' Por qué esta y cuánto costaría cada opción con la otra marca: en Supuestos del cálculo.' : '';
  const poner = (campo: 'bateria_id' | 'inversor_id', valor: string) => {
    const nuevo = { bateria_id: eq?.bateria_id ?? null, inversor_id: eq?.inversor_id ?? null, [campo]: valor || null };
    const despues = nuevo.bateria_id === null && nuevo.inversor_id === null ? null : nuevo;
    cambiar('/parametros/equipo_respaldo', eq, despues, campo === 'bateria_id' ? 'Batería del respaldo' : 'Inversor del respaldo');
  };
  return (
    <div className="rejilla-2">
      <CampoSelect
        etiqueta="Batería del respaldo"
        value={eq?.bateria_id ?? ''}
        disabled={ocupado || !vista}
        onChange={(e) => poner('bateria_id', e.target.value)}
        ayuda={n ? `Hoy: ${n.bateria.marca} ${n.bateria.modelo}.${porQue}` : undefined}
        opciones={[{ valor: '', texto: 'La más barata compatible (omisión)' }, ...bats.map((b) => ({ valor: b.id, texto: `${b.marca} ${b.modelo} · ${numero(b.kwh_utiles_por_modulo, 2)} kWh útiles` }))]}
      />
      <CampoSelect
        etiqueta="Inversor del respaldo"
        value={eq?.inversor_id ?? ''}
        disabled={ocupado || !vista}
        onChange={(e) => poner('inversor_id', e.target.value)}
        ayuda={n?.inversor ? `Hoy: ${n.inversor.marca} ${n.inversor.modelo}.` : undefined}
        opciones={[{ valor: '', texto: 'El compatible más barato que aguante la casa (omisión)' }, ...invs.map((v) => ({ valor: v.id, texto: `${v.marca} ${v.modelo} · ${numero(v.kw_continuos)} kW` }))]}
      />
    </div>
  );
}

const NIVEL_AISLADO: Readonly<Record<NivelRespaldo, string>> = { esencial: 'Esencial', confort: 'Confort', completa: 'Casa completa' };

/** Rentabilidad del RESPALDO (solo Diego: el servidor no la manda a nadie más, vista.ts). Al interno, solo el color. */
function RentabilidadRespaldo({ n }: { n: NivelRespaldoCalculado }) {
  const fuente = n.solo_respaldo ?? n;
  const r = fuente.rentabilidad ?? null;
  const color = colorSemaforo(fuente);
  if (!r) return color ? (
    <span className={`semaforo semaforo--${color}`} data-semaforo={color}>
      <span className="semaforo__punto" aria-hidden />
      {ETIQUETA_SEMAFORO[color]}
    </span>
  ) : null;
  return (
    <div className={`rentabilidad rentabilidad--${r.semaforo}`} aria-label="Rentabilidad del respaldo (solo Diego)" data-rentabilidad-respaldo>
      <p className="nota-suave">
        <span className={`semaforo semaforo--${r.semaforo}`}>
          <span className="semaforo__punto" aria-hidden />
          {ETIQUETA_SEMAFORO[r.semaforo]}
        </span>{' '}
        Margen {numero(r.margen_contado_pct, 1)} % · {r.utilidad_contado_sin_iva < 0 ? `pierde ${dinero(-r.utilidad_contado_sin_iva)}` : `deja ${dinero(r.utilidad_contado_sin_iva)}`} · costo {dinero(r.costo_total_sin_iva)} sin IVA
      </p>
    </div>
  );
}

/** UN par batería + inversor para toda la propuesta (ADENDA z22-1), entre los compatibles con precio. ADENDA z48-2 (Diego, 28-oct):
 *  CUALQUIER vendedor cambia el INVERSOR y el MODELO DE BATERÍA, cada uno en su selector (antes un solo selector de pares). Al
 *  cambiar uno se queda el otro si hacen par; si no, el cotizador pone el compatible más barato. Con otro equipo, las baterías y
 *  las horas de cada opción vuelven a las automáticas. El precio lo recalcula el servidor. */
function SelectorParRespaldo({ r, parametros, mandar, ocupado, aislado = false }: { r: ResultadoMotor; parametros: ParametrosCalculo; mandar: (hacer: (c: Cotizacion) => EdicionCampo[]) => Promise<boolean>; ocupado: boolean; aislado?: boolean }) {
  const fijados = parametros.modulos_respaldo ?? null;
  const horas = parametros.horas_respaldo ?? null;
  const eq = parametros.equipo_respaldo ?? null;
  const pares = r.respaldo?.pares ?? [];
  const n0 = r.respaldo?.niveles.find((n) => n.id === r.respaldo?.recomendado) ?? r.respaldo?.niveles[0] ?? null;
  const batHoy = eq?.bateria_id ?? n0?.bateria.id_tabla ?? null;
  const invHoy = eq?.inversor_id ?? n0?.inversor?.id_tabla ?? null;
  // el nombre del par es «batería + inversor» (respaldo.ts)
  const parte = (nombre: string, k: 0 | 1) => {
    const i = nombre.indexOf(' + ');
    return i < 0 ? nombre : k === 0 ? nombre.slice(0, i) : nombre.slice(i + 3);
  };
  const lista = (k: 'bateria_id' | 'inversor_id') => [...new Map(pares.map((p) => [p[k], parte(p.nombre, k === 'bateria_id' ? 0 : 1)])).entries()].map(([valor, texto]) => ({ valor, texto }));
  const poner = (bateria_id: string | null, inversor_id: string | null, motivo: string) =>
    void mandar(() => {
      const ed = [reemplazo('/parametros/equipo_respaldo', eq, bateria_id || inversor_id ? { bateria_id, inversor_id } : null, motivo)];
      if (fijados) ed.push(reemplazo('/parametros/modulos_respaldo', fijados, null, 'Con otro equipo, las baterías de cada opción vuelven a las automáticas'));
      if (horas) ed.push(reemplazo('/parametros/horas_respaldo', horas, null, 'Con otro equipo, las horas de cada opción vuelven a las automáticas'));
      return ed;
    });
  const hacenPar = (b: string | null, v: string | null) => !!b && !!v && pares.some((p) => p.bateria_id === b && p.inversor_id === v);
  const hoy = (m: { marca: string; modelo: string } | null | undefined) => (m ? ` (hoy: ${m.marca} ${m.modelo})` : '');
  return (
    <div className="pila pila--chica" role="group" aria-label="Inversor y batería de toda la propuesta">
      <CampoSelect
        // Diego, 28-oct: en un aislado es el inversor que trabaja SIN red (nunca uno de red)
        etiqueta={aislado ? 'Inversor aislado, sin red (uno para toda la propuesta)' : 'Inversor híbrido (uno para toda la propuesta)'}
        value={eq?.inversor_id ?? ''}
        disabled={ocupado}
        onChange={(e) => {
          const v = e.target.value || null;
          poner(v && hacenPar(batHoy, v) ? batHoy : eq?.bateria_id && !v ? eq.bateria_id : null, v, 'Inversor híbrido del respaldo');
        }}
        opciones={[{ valor: '', texto: `El compatible más barato${hoy(n0?.inversor)}` }, ...lista('inversor_id')]}
      />
      <CampoSelect
        etiqueta="Modelo de batería"
        value={eq?.bateria_id ?? ''}
        disabled={ocupado}
        onChange={(e) => {
          const b = e.target.value || null;
          poner(b, b && hacenPar(b, invHoy) ? invHoy : eq?.inversor_id && !b ? eq.inversor_id : null, 'Modelo de batería del respaldo');
        }}
        opciones={[{ valor: '', texto: `La compatible más barata${hoy(n0?.bateria)}` }, ...lista('bateria_id')]}
      />
    </div>
  );
}

/** ADENDA z48-2 (Diego, 28-oct): las HORAS de respaldo de UNA opción del híbrido. El vendedor escribe las horas y el servidor pone la
 *  cantidad de baterías que deja las horas más cerca (nunca 0) y recalcula el precio; borra la cantidad a mano de esa opción (la
 *  cantidad a mano manda sobre las horas). Vacío = las automáticas. */
function HorasDeOpcion({ nivel, parametros, mandar, ocupado, nombre }: { nivel: NivelRespaldoCalculado; parametros: ParametrosCalculo; mandar: (hacer: (c: Cotizacion) => EdicionCampo[]) => Promise<boolean>; ocupado: boolean; nombre: string }) {
  const horas = parametros.horas_respaldo ?? null;
  const fijados = parametros.modulos_respaldo ?? null;
  const escritas = horas?.[nivel.id] ?? null;
  const [texto, setTexto] = useState(escritas === null ? '' : String(escritas));
  useEffect(() => setTexto(escritas === null ? '' : String(escritas)), [escritas]);
  const h = texto.trim() === '' ? null : numeroDe(texto);
  const malo = h !== null && !(h > 0 && h <= 72);
  const aplicar = () => {
    if (malo || h === escritas || (texto.trim() !== '' && h === null)) return;
    void mandar(() => {
      const nuevo = { esencial: null, confort: null, completa: null, ...(horas ?? {}), [nivel.id]: h };
      const ed = [reemplazo('/parametros/horas_respaldo', horas, Object.values(nuevo).every((x) => x === null) ? null : nuevo, h === null ? `${nombre}: horas automáticas` : `${nombre}: ${numero(h, h % 1 ? 1 : 0)} h de respaldo`)];
      if (fijados?.[nivel.id] != null) {
        const m = { ...fijados, [nivel.id]: null };
        ed.push(reemplazo('/parametros/modulos_respaldo', fijados, Object.values(m).every((x) => x === null) ? null : m, `${nombre}: la cantidad sale de las horas`));
      }
      return ed;
    });
  };
  return (
    <CampoTexto
      etiqueta="Horas de respaldo"
      ayuda={`Escribe las horas y se pone la cantidad de baterías más cercana. Hoy: de ${nivel.horas.min} a ${nivel.horas.max} h.`}
      error={malo ? 'Escribe de 0.5 a 72 horas.' : undefined}
      inputMode="decimal"
      sufijo="h"
      value={texto}
      placeholder={String(nivel.horas.max)}
      disabled={ocupado}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={aplicar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') aplicar();
      }}
    />
  );
}

/** ADENDA z48-4 (Diego, 28-oct): «Agregar baterías» en una cotización de SOLO PANELES de casa o comercio (no industria): la vuelve
 *  híbrido (toda la casa conectada) sin rehacerla, con la misma edición del cuestionario que «Editar cuestionario» (conEnfoque +
 *  borradorCuestionario). El servidor prende las 1 a 2 h por opción (recalculo.ts → conRespaldoNuevo) y recalcula el precio. */
function AgregarBaterias() {
  const { mandar, error, setError, ocupado } = usarEditarParametros();
  const agregar = () =>
    void mandar((c) => {
      const q = c.borrador.cuestionario;
      const f = formaDesde(q);
      const n = conEnfoque({ ...f, clienteTipo: f.clienteTipo ?? (q.tipo === 'comercio' ? 'comercio' : 'casa') }, { queCotiza: 'paneles_baterias' });
      return [reemplazo('/cuestionario', q, borradorCuestionario(n), 'Agregar baterías: híbrido con toda la casa conectada (1 a 2 h por opción)')];
    });
  return (
    <section className="respaldo-opciones pila pila--chica" aria-label="Agregar baterías">
      <p className="nota-suave">
        <BatteryCharging aria-hidden /> ¿El cliente quiere respaldo para apagones? Cada opción lleva su inversor híbrido y baterías para 1 a 2 horas con toda la casa conectada; el precio se ajusta solo.
      </p>
      <div>
        <Boton chico variante="secundario" icono={<BatteryCharging aria-hidden />} disabled={ocupado} onClick={agregar}>
          Agregar baterías
        </Boton>
      </div>
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </section>
  );
}

/** E4b (Diego, 24-oct): con baterías POR OPCIÓN, lo que es de toda la propuesta va ARRIBA de las tarjetas (el par batería +
 *  inversor y «volver a las automáticas»); las baterías de cada opción se mueven en su tarjeta. */
function EquipoPorOpcion({ r, parametros, puedeEditar, aislado = false }: { r: ResultadoMotor; parametros: ParametrosCalculo; puedeEditar: boolean; aislado?: boolean }) {
  const { mandar, error, setError, ocupado, fijados } = usarModulosRespaldo(r, parametros);
  const pares = r.respaldo?.pares ?? [];
  return (
    <section className="respaldo-opciones pila pila--chica" aria-label="Baterías de las opciones">
      <p className="nota-suave">
        <BatteryCharging aria-hidden />{' '}
        {aislado
          ? 'Aislado, sin red de CFE: cada opción es el sistema completo (sus paneles, su inversor aislado y sus baterías) con un solo precio, el del PDF. Sube o baja las baterías en su tarjeta; las noches son con lo conectado.'
          : 'Cada opción lleva su inversor híbrido y sus baterías (súbelas o bájalas en su tarjeta). Horas con toda la casa conectada (con aires, menos).'}
      </p>
      {puedeEditar && pares.length > 0 && <SelectorParRespaldo r={r} parametros={parametros} mandar={mandar} ocupado={ocupado} aislado={aislado} />}
      {puedeEditar && fijados && Object.values(fijados).some((x) => x !== null) && (
        <Boton chico variante="fantasma" icono={<RefreshCw aria-hidden />} disabled={ocupado} onClick={() => void mandar(() => [reemplazo('/parametros/modulos_respaldo', fijados, null, 'Baterías de las opciones: las que calcula el cotizador')])}>
          Volver a las baterías que calcula el cotizador
        </Boton>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </section>
  );
}

/** BATERÍAS EN OPCIONES (Diego, 14-oct-2026 22:30, MANDA; 16-oct: «en Opciones no te dice cuántas baterías tiene ni el
 *  respaldo»). Cada nivel de respaldo tan claro como una opción de paneles: cuántas baterías, modelo de batería e inversor,
 *  kWh útiles, horas con toda la casa conectada y precio (contado con IVA y lista, como lo ve el cliente). Con paneles, el
 *  respaldo va APARTE y sin retorno (es seguridad, no ahorro; el retorno es el de la opción de paneles). El vendedor sube o
 *  baja las baterías de un nivel, lo quita (queda al menos uno) y elige UN par batería + inversor entre los compatibles
 *  (ADENDA z22-1); el servidor recalcula horas, kWh y precio, y el PDF sale con eso. */
function NivelesRespaldo({ r, parametros, puedeEditar }: { r: ResultadoMotor; parametros: ParametrosCalculo; puedeEditar: boolean }) {
  const { mandar, error, setError, ocupado, ponerModulos: poner, fijados } = usarModulosRespaldo(r, parametros);
  const res = r.respaldo!;
  const hib = res.hibrido ?? null;
  // solo respaldo (Diego, 24-oct, decisión 3): los bancos se llaman «Hasta N horas» también en un resultado de antes por cargas
  const casa = res.alcance !== 'cargas' || res.tipo === 'respaldo';
  const pares = res.pares ?? [];
  const nombreDe = (n: NivelRespaldoCalculado) => (casa ? `Hasta ${n.horas.max} horas` : NIVEL_AISLADO[n.id]);
  const ponerModulos = (n: NivelRespaldoCalculado, valor: number) => poner(n.id, valor, valor === 0 ? `Quitar el respaldo ${nombreDe(n)}` : `Baterías del respaldo ${nombreDe(n)}: ${valor}`);
  return (
    <section className="respaldo-opciones pila pila--chica" aria-label="Respaldo con baterías">
      <div className="respaldo-opciones__cabeza">
        <h3 className="opcion__nombre">
          <BatteryCharging aria-hidden /> Respaldo con baterías
        </h3>
        <p className="nota-suave">
          {casa ? 'Toda la casa conectada; horas estimadas con su consumo (con aires, menos).' : 'Lo que conecta el cliente; horas con lo conectado.'}
          {hib ? ' Precio del respaldo aparte de los paneles y sin retorno: es seguridad, no ahorro.' : ''}
          {hib?.acople === 'cd' ? ` Un solo inversor híbrido recibe los paneles (sale ${hib.inversor_red ?? 'el de red'}).` : ''}
        </p>
      </div>
      {puedeEditar && pares.length > 0 && <SelectorParRespaldo r={r} parametros={parametros} mandar={mandar} ocupado={ocupado} />}
      <div className="opciones">
        {res.niveles.map((n, k) => {
          const pr = n.solo_respaldo?.precio ?? n.precio;
          const recomendado = res.recomendado === n.id;
          const antes = res.niveles[k - 1]?.bateria.modulos ?? 0;
          const despues = res.niveles[k + 1]?.bateria.modulos ?? Number.POSITIVE_INFINITY;
          const color = colorSemaforo(n.solo_respaldo ?? n);
          return (
            <article key={n.id} className={['opcion', recomendado ? 'opcion--recomendada' : '', color ? `opcion--${color}` : ''].join(' ')} aria-label={`Respaldo ${nombreDe(n)}`}>
              <header className="opcion__cabeza">
                <h4 className="opcion__nombre">
                  {nombreDe(n)}
                  {recomendado && (
                    <span className="opcion__recomendada">
                      <Star aria-hidden /> Recomendado
                    </span>
                  )}
                </h4>
              </header>
              <div className="opcion__precio">
                <p className="opcion__etiqueta">{hib ? 'Respaldo, contado con IVA' : 'Contado con IVA'}</p>
                <p className="opcion__cifra">{pr ? dinero(pr.contado) : 'Sin precio en la tabla'}</p>
                {pr && <p className="opcion__lista">Con tarjeta de crédito: {dinero(pr.lista)} con IVA</p>}
                {hib && n.precio && <p className="opcion__subtotal">Con los paneles: {dinero(n.precio.contado)} de contado</p>}
              </div>
              <dl className="opcion__datos">
                <div className="opcion__dato--ancho">
                  <dt>Baterías</dt>
                  <dd>
                    {n.bateria.modulos} × {n.bateria.marca} {n.bateria.modelo} · {pct(n.bateria.kwh_utiles)} kWh útiles
                  </dd>
                </div>
                <div className="opcion__dato--ancho">
                  <dt>Inversor</dt>
                  <dd>{n.inversor ? `${n.inversor.cantidad} × ${n.inversor.marca} ${n.inversor.modelo} · ${pct(n.inversor.kw_continuos * n.inversor.cantidad)} kW` : 'Falta en la tabla'}</dd>
                </div>
                <div className="opcion__dato--ancho">
                  <dt>{casa ? 'Si se va la luz, con toda la casa' : 'Con lo conectado'}</dt>
                  <dd>
                    De {n.horas.min} a {n.horas.max} h
                  </dd>
                </div>
              </dl>
              <RentabilidadRespaldo n={n} />
              {puedeEditar && (
                <div className="respaldo-opciones__baterias" role="group" aria-label={`Baterías del respaldo ${nombreDe(n)}`}>
                  <Boton chico variante="fantasma" icono={<Minus aria-hidden />} aria-label="Una batería menos" disabled={ocupado || n.bateria.modulos - 1 <= antes || n.bateria.modulos <= 1} onClick={() => ponerModulos(n, n.bateria.modulos - 1)} />
                  <span>
                    {n.bateria.modulos} {n.bateria.modulos === 1 ? 'batería' : 'baterías'}
                  </span>
                  <Boton chico variante="fantasma" icono={<Plus aria-hidden />} aria-label="Una batería más" disabled={ocupado || n.bateria.modulos + 1 >= despues} onClick={() => ponerModulos(n, n.bateria.modulos + 1)} />
                  <Boton chico variante="fantasma" icono={<Trash2 aria-hidden />} disabled={ocupado || res.niveles.length <= 1} onClick={() => ponerModulos(n, 0)}>
                    Quitar
                  </Boton>
                </div>
              )}
            </article>
          );
        })}
      </div>
      {puedeEditar && fijados && Object.values(fijados).some((x) => x !== null) && (
        <Boton chico variante="fantasma" icono={<RefreshCw aria-hidden />} disabled={ocupado} onClick={() => void mandar(() => [reemplazo('/parametros/modulos_respaldo', fijados, null, 'Baterías del respaldo: las que calcula el cotizador')])}>
          Volver a las baterías que calcula el cotizador
        </Boton>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </section>
  );
}

/** RECORTE DE PUNTA (ADENDA z54/z55-2, 29-oct-2026): la batería grande en GDMTH, de solo lectura con sus cifras del motor. Lo grande
 *  son los kW por temporada (z34-5); el ahorro, al mes y en rango (z54: toda cifra al mes); el retorno en 4 renglones (candado 4);
 *  el semáforo de retorno lo ven todos y el de margen, según el rol. − / + cambia los gabinetes (parametros.recorte_gabinetes). */
export function TarjetaRecorte({ r, parametros, puedeEditar }: { r: ResultadoMotor; parametros: ParametrosCalculo; puedeEditar: boolean }) {
  const { mandar, error, setError, ocupado } = usarEditarParametros();
  const k = r.recorte_punta ?? null;
  if (!k)
    return (
      <div className="aviso-ancho aviso-ancho--ambar" role="alert" aria-label="Recorte de punta sin calcular">
        <strong>El recorte de punta no se pudo calcular.</strong> Revisa el aviso rojo de arriba (12 recibos GDMTH, tarifa, tipo de cambio).
      </div>
    );
  const anios = (x: number) => (x >= 99 ? 'más de 25 años' : `${numero(x, 1)} años`);
  const e = k.equipo;
  const gab = parametros.recorte_gabinetes ?? null;
  const cambiarGabinetes = (n: number | null) =>
    void mandar((c) => [reemplazo('/parametros', c.borrador.parametros, { ...c.borrador.parametros, recorte_gabinetes: n }, n === null ? 'El cotizador elige los gabinetes (mejor retorno)' : `Recorte de punta con ${n} gabinetes`)]);
  const colorRetorno = k.semaforo_retorno;
  const margen = k.rentabilidad ? colorSemaforo({ rentabilidad: k.rentabilidad } as ConMargen) : (k.semaforo_margen ?? null);
  return (
    <section className="opcion opcion--recomendada" aria-label="Recorte de punta" data-recorte-punta>
      <header className="pila pila--chica">
        <p className="nota-suave">Recorte de punta · {e.gabinetes} {e.gabinetes === 1 ? 'gabinete' : 'gabinetes'} {e.marca} {e.modelo} ({numero(e.kw_pcs)} kW / {numero(e.kwh_nominales)} kWh)</p>
        <p className="opcion__cifra" data-recorte-kw>
          de {numero(k.kw_temporada.min)} a {numero(k.kw_temporada.max)} kW <small>de tu punta, según la temporada</small>
        </p>
        {/* separados (verificación de hoy70: «Retorno en verde●Rentable» salía pegado) */}
        <p className="fila-botones">
          {colorRetorno ? (
            <span className={`semaforo semaforo--${colorRetorno}`} data-semaforo-retorno={colorRetorno}>
              <span className="semaforo__punto" aria-hidden />
              {colorRetorno === 'verde' ? 'Retorno en verde' : e.vencido ? 'Retorno en amarillo: precio del equipo por confirmar' : 'Retorno en amarillo: ofrécelo con el rango'}
            </span>
          ) : (
            <Chip tono="rojo">No se ofrece</Chip>
          )}
          {margen && (
            <span className={`semaforo semaforo--${margen}`} data-semaforo={margen}>
              <span className="semaforo__punto" aria-hidden />
              {ETIQUETA_SEMAFORO[margen]}
              {k.rentabilidad ? ` · ${numero(k.rentabilidad.margen_contado_pct, 1)} % de margen` : ''}
            </span>
          )}
        </p>
      </header>
      <p>
        Ahorro estimado: de <strong>{dinero(k.escenarios.solo_energia.mes)}</strong> a <strong>{dinero(k.escenarios.todas.mes)}</strong> al mes, con IVA{' '}
        <span className="nota-suave">({dinero(k.escenarios.solo_energia.total)} a {dinero(k.escenarios.todas.total)} al año)</span>
      </p>
      <ul className="lista-simple" aria-label="Se paga sola en">
        <li>Recorta todas las puntas: <strong>{anios(k.retorno.todas)}</strong></li>
        <li>Se escapan 2 meses: <strong>{anios(k.retorno.fallan2)}</strong></li>
        <li>Se escapan 6 meses: <strong>{anios(k.retorno.fallan6)}</strong></li>
        <li>Solo con la energía: <strong>{anios(k.retorno.solo_energia)}</strong></li>
        <li className="nota-suave">Sin que suba la luz (todas las puntas): {anios(k.retorno.todas_sin_alza)}</li>
      </ul>
      <p className="nota-suave">
        Baja la capacidad en {k.meses_que_recortan} de 12 meses · su punta manda en {k.meses_manda_punta}
        {/* «zona muerta» en palabras del vendedor, y solo si la hay (verificación de hoy70) */}
        {k.zona_muerta_kw > 0 ? ` · los primeros ${numero(k.zona_muerta_kw, 0)} kW que recorta no bajan el recibo (ahí manda su consumo)` : ''} · ahorro con las cuotas de CFE de {k.cuotas_ahorro_mes}
      </p>
      <p>
        Precio de contado, con IVA: <strong>{dinero(k.precio.contado)}</strong> <span className="nota-suave">(precio único: {dinero(k.precio.contado_sin_iva)} + IVA)</span>
        {e.vencido && <Chip tono="ambar">{e.precio_de === 'lista' ? `precio de la lista del mes: la cotización del gabinete venció el ${e.vence_el}` : `cotización del gabinete vencida el ${e.vence_el}`}: por confirmar</Chip>}
        {e.transformador_kva && <Chip tono="ambar">{textoTransformador(e.transformador_kva, e.transformador_unidades)} {(e.transformador_unidades ?? 1) > 1 ? 'estimados' : 'estimado'}</Chip>}
      </p>
      {k.finsus_referencia && <FinsusOpcion f={k.finsus_referencia} />}
      {puedeEditar && (
        <div className="fila-botones" aria-label="Gabinetes">
          <Boton chico variante="fantasma" icono={<Minus aria-hidden />} disabled={ocupado || e.gabinetes <= 1} onClick={() => cambiarGabinetes(e.gabinetes - 1)}>
            Un gabinete menos
          </Boton>
          <Boton chico variante="fantasma" icono={<Plus aria-hidden />} disabled={ocupado || e.gabinetes >= 10} onClick={() => cambiarGabinetes(e.gabinetes + 1)}>
            Un gabinete más
          </Boton>
          {gab !== null && (
            <Boton chico variante="fantasma" disabled={ocupado} onClick={() => cambiarGabinetes(null)}>
              Que lo elija el cotizador
            </Boton>
          )}
        </div>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </section>
  );
}

function RevisionIA() {
  const { cot, ejecutar } = usarCotizacion();
  const { config, puedeEn } = usarUsuario();
  const [revisando, setRevisando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const rev = cot.borrador.revision_ia;
  const sinLlave = config.ia.modo === 'sin_llave';
  const revisar = async () => {
    setRevisando(true);
    setError(null);
    try {
      await ejecutar((c) => api.revisarConIA(c.id, c.revision));
    } catch (e) {
      setError(e);
    } finally {
      setRevisando(false);
    }
  };
  return (
    <div className={['revision-ia', rev ? `revision-ia--${rev.veredicto}` : ''].join(' ')}>
      <div className="revision-ia__texto">
        {rev ? (
          <>
            <p className="revision-ia__veredicto">
              Revisión de rentabilidad: <strong>{ETIQUETA_VEREDICTO[rev.veredicto]}</strong>
              {rev.simulada && <span className="tabla__marca">simulada</span>}
            </p>
            <p className="revision-ia__resumen">{rev.resumen}</p>
            <p className="nota-suave">{fechaHoraCorta(rev.revisado_en)}</p>
          </>
        ) : (
          <>
            <p className="revision-ia__veredicto">Revisión de rentabilidad con IA</p>
            <p className="revision-ia__resumen">
              {sinLlave ? 'Sin llave de IA: el semáforo de reglas sigue funcionando.' : 'La IA compara precio contra costos (equipo, mano de obra, UVIE + UI, adicionales) y te da un resumen de la rentabilidad.'}
            </p>
          </>
        )}
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      </div>
      {puedeEn('editar_cotizaciones', cot) && (
        <Boton variante="noche" icono={<Sparkles aria-hidden />} cargando={revisando} disabled={sinLlave} onClick={() => void revisar()}>
          {rev ? 'Revisar otra vez' : 'Revisar con IA'}
        </Boton>
      )}
    </div>
  );
}

export function SeccionOpciones() {
  const { cot, ejecutar } = usarCotizacion();
  const { puedeEn } = usarUsuario();
  const b = cot.borrador;
  const r = b.resultado;
  const [vista, setVista] = useState<OpcionId>(r?.recomendada ?? 'exacta');
  const [recalculando, setRecalculando] = useState(false);
  const [aplicandoPrecio, setAplicandoPrecio] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const opcionVista = useMemo(() => r?.opciones.find((o) => o.id === vista) ?? r?.opciones.find((o) => o.id === r.recomendada) ?? null, [r, vista]);
  const puedeEditar = puedeEn('editar_cotizaciones', cot);

  const recalcular = async () => {
    setRecalculando(true);
    setError(null);
    try {
      await ejecutar((c) => api.calcular(c.id, c.revision));
    } catch (e) {
      setError(e);
    } finally {
      setRecalculando(false);
    }
  };

  if (!r) {
    // Etapa 4: la tabla leída de los recibos espera que el vendedor la autorice (no es un error: es el paso que sigue).
    const autorizar = b.avisos.find((a) => a.codigo === 'LECTURA_POR_AUTORIZAR');
    if (autorizar)
      return (
        <div className="pila">
          <Vacio
            titulo="Falta autorizar lo que se leyó"
            accion={
              <Boton variante="principal" onClick={() => navegar(`/cotizaciones/${cot.id}/revision`)}>
                Revisar y autorizar
              </Boton>
            }
          >
            Antes de calcular, revisa la tabla que se leyó de los recibos contra el recibo, corrige lo que haga falta y autorízala. En cuanto la autorices, aquí aparecen las opciones.
          </Vacio>
        </div>
      );
    const motivo = b.avisos.find((a) => (a.origen === 'sistema' || a.origen === 'motor') && a.nivel === 'rojo' && !a.resuelto);
    return (
      <div className="pila">
        <Vacio
          titulo="Todavía no hay opciones"
          accion={
            <div className="fila-botones">
              {!b.cuestionario.consumo.es_nuevo && (
                <Boton onClick={() => navegar(`/cotizaciones/${cot.id}/${lecturaConDatos(b.lecturas.A) ? 'revision' : 'recibos'}`)}>
                  {lecturaConDatos(b.lecturas.A) ? 'Revisar lo leído' : 'Subir recibos'}
                </Boton>
              )}
              {puedeEditar && (
                <Boton variante="noche" icono={<RefreshCw aria-hidden />} cargando={recalculando} onClick={() => void recalcular()}>
                  Calcular otra vez
                </Boton>
              )}
            </div>
          }
        >
          {motivo ? motivo.mensaje : 'Faltan los consumos del cliente para calcular.'}
        </Vacio>
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      </div>
    );
  }

  // E4a de BATERÍAS POR OPCIÓN (Diego, 24-oct-2026, decisión 3): SOLO RESPALDO = los bancos de baterías («Hasta N horas») con
  // su híbrido; sin tarjetas de paneles, sin su gráfica y sin sus ajustes (el motor calcula las opciones, pero no se venden).
  // Recorte de punta (z54): la batería grande; las opciones de paneles son de referencia y no se enseñan (como el solo respaldo)
  const recorte = r.recorte_punta !== undefined;
  const soloRespaldo = r.respaldo?.tipo === 'respaldo' || recorte;
  // E4b: por opción (con paneles), las baterías van en la tarjeta de cada opción y la sección de niveles de abajo no se pinta
  const bateriasEnTarjetas = !soloRespaldo && !!r.respaldo?.por_opcion && r.respaldo.niveles.every((n) => n.sistema);
  // Diego, 28-oct (capturas de Miguel): un AISLADO es UN solo bloque de 3 opciones, cada una el sistema completo; nunca las
  // tarjetas de paneles arriba y «Esencial / Confort / Casa completa» abajo. Si el cálculo no viene así, se dice fuerte.
  const esAislado = b.cuestionario.tipo === 'aislado';
  const consumoAnual = r.periodos.reduce((s, p) => s + p.consumo_kwh, 0);
  const pagoAnual = r.periodos.reduce((s, p) => s + p.pago_hoy, 0);
  // OPTIMUS (Diego, 12-oct noche): el precio no es el que le toca al vendedor de hoy; no cambia solo: este botón lo aplica.
  const avisoOptimus = b.avisos.find((a) => a.codigo === 'PRECIO_OPTIMUS' && a.nivel === 'rojo' && !a.resuelto) ?? null;
  const aplicarPrecio = async () => {
    setAplicandoPrecio(true);
    setError(null);
    try {
      await ejecutar((c) => api.aplicarPrecioVendedor(c.id, c.revision));
    } catch (e) {
      setError(e);
    } finally {
      setAplicandoPrecio(false);
    }
  };

  return (
    <div className="pila pila--grande">
      <BotonPromo opciones={r.opciones} recomendada={r.recomendada} puedeEditar={puedeEditar} />
      {avisoOptimus && (
        <div className="aviso-ancho aviso-ancho--ambar pila" role="alert" aria-label="El precio no es el de su vendedor">
          <p>
            <strong>Precio por revisar.</strong> {avisoOptimus.mensaje}
          </p>
          {puedeEditar && (
            <div className="fila-botones">
              <Boton variante="principal" cargando={aplicandoPrecio} onClick={() => void aplicarPrecio()}>
                {/«Aplicar el precio normal»/.test(avisoOptimus.mensaje) ? 'Aplicar el precio normal' : 'Aplicar el precio de Optimus'}
              </Boton>
            </div>
          )}
        </div>
      )}
      <div className="resumen-calculo">
        <p>
          {b.cuestionario.tipo === 'aislado' ? (
            // z33: un aislado no tiene CFE: solo el consumo con que se calculan sus paneles y sus baterías
            <>Consumo de la casa: <strong>{kwh(consumoAnual)}</strong> al año · aislado, sin red de CFE</>
          ) : r.base_futura ? (
            // ADENDA z52: el cálculo no es el recibo tal cual (negocio en PDBT y/o aumento de consumo): las dos cifras, rotuladas
            <>
              {r.base_futura.aumento ? (
                <>Consumo con el aumento: <strong>{kwh(consumoAnual)}</strong> al año (recibo {kwh(r.base_futura.recibo_real.kwh_anual)} + {kwh(r.base_futura.aumento.kwh_anual)}, {r.base_futura.aumento.motivo})</>
              ) : (
                <>Consumo del cliente: <strong>{kwh(consumoAnual)}</strong> al año</>
              )}
              {/* D3 (verificación del 28-oct): «sin paneles» sigue en la tarifa del recibo; con cambio, solo «con paneles» va en PDBT/GDBT */}
              {r.base_futura.aumento ? (
                <>
                  {' · '}pagaría sin paneles <strong>{dinero(pagoAnual)}</strong> al año en {r.base_futura.recibo_real.tarifa} (hoy paga {dinero(r.base_futura.recibo_real.pago_anual)})
                </>
              ) : (
                <>
                  {' · '}paga hoy <strong>{dinero(pagoAnual)}</strong> al año a CFE en {r.base_futura.recibo_real.tarifa}
                </>
              )}
              {' · '}
              {r.base_futura.cambio_tarifa ? `con paneles, tarifa ${r.tarifa} (CFE lo cambia al conectarlos)` : `tarifa ${r.tarifa}`} · {r.facturacion}
            </>
          ) : (
            <>Consumo del cliente: <strong>{kwh(consumoAnual)}</strong> al año · paga hoy <strong>{dinero(pagoAnual)}</strong> al año a CFE · tarifa {r.tarifa} · {r.facturacion}</>
          )}
          {/* ADENDA z62(1): los meses escritos a mano sin recibo también son estimados (solo los meses con recibo son reales). */}
          {(r.periodos.some((p) => p.origen === 'estimado') || (!b.cuestionario.dos_medidores && periodosEscritosSinRecibo(b.lecturas.A, r.periodos).length > 0)) && <Chip tono="ambar">con periodos estimados</Chip>}
          {/* hoy85: si el PDF no puede marcarlos (menos de 3 meses de recibo, baterías, dos medidores), se dice aquí también. */}
          {(() => {
            const porque = porqueEscritosVanComoRecibo(b);
            return porque ? (
              <span title={`En el PDF los meses escritos a mano sin recibo van como recibo: ${porque}.`}>
                <Chip tono="ambar">meses a mano: el PDF los imprime como recibo</Chip>
              </span>
            ) : null;
          })()}
        </p>
        {(r.servicio || (!soloRespaldo && r.estructura && r.estructura.tipo !== 'losa_lamina')) && (
          // Diego, 12-oct: el servicio de CFE con que se eligieron los inversores y la estructura (sin cifras de costo)
          <p className="nota-suave">
            {r.servicio && (
              <>
                Servicio de CFE: <strong>{r.servicio.como}</strong>
                {r.servicio.deducido ? ' · confírmalo en la visita' : ''}
              </>
            )}
            {r.servicio && !soloRespaldo && r.estructura && r.estructura.tipo !== 'losa_lamina' ? ' · ' : ''}
            {!soloRespaldo && r.estructura && r.estructura.tipo !== 'losa_lamina' && (
              <>
                Estructura: <strong>{ETIQUETA_ESTRUCTURA[r.estructura.tipo]}</strong> (incluida en el precio{r.estructura.por_confirmar ? '; mano de obra por confirmar' : ''})
              </>
            )}
          </p>
        )}
        {puedeEditar && (
          <Boton chico variante="fantasma" icono={<Calculator aria-hidden />} cargando={recalculando} onClick={() => void recalcular()}>
            Recalcular
          </Boton>
        )}
      </div>
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />

      {/* Etapa 3 (Diego, 12-oct): la generación por kW de esta obra (base de Diego, sol del municipio y lo que ajusta el vendedor) */}
      {!soloRespaldo && <GeneracionObra cuestionario={b.cuestionario} parametros={b.parametros} r={r} puedeEditar={puedeEditar} />}

      {!soloRespaldo && (
        <div className="pestanas solo-celular-flex" role="tablist" aria-label="Opción">
          {r.opciones.map((o) => (
            <button key={o.id} type="button" role="tab" aria-selected={opcionVista?.id === o.id} className={['pestanas__item', opcionVista?.id === o.id ? 'es-activo' : ''].join(' ')} onClick={() => setVista(o.id)}>
              {colorSemaforo(nivelDeOpcion(r, o.id) ?? o) && <span className={`semaforo__punto semaforo__punto--${colorSemaforo(nivelDeOpcion(r, o.id) ?? o)}`} aria-hidden />}
              {ETIQUETA_OPCION[o.id]}
              {r.recomendada === o.id && <Star aria-label="recomendada" className="pestanas__estrella" />}
            </button>
          ))}
        </div>
      )}

      {puedeEditar && !cot.multisitio && !r.respaldo && (b.cuestionario.tipo === 'casa' || b.cuestionario.tipo === 'comercio') && !b.cuestionario.ampliacion.es_ampliacion && <AgregarBaterias />}
      {esAislado && !bateriasEnTarjetas && (
        <div className="aviso-ancho aviso-ancho--ambar pila" role="alert" aria-label="Aislado sin su sistema completo">
          <p>
            <strong>Este aislado no salió como sistema completo.</strong> Cada opción debe llevar sus paneles, su inversor aislado (sin red) y sus baterías con un solo precio, el del PDF. Recalcula; si sigue así, avísale a Diego.
          </p>
          {puedeEditar && (
            <div className="fila-botones">
              <Boton variante="principal" icono={<Calculator aria-hidden />} cargando={recalculando} onClick={() => void recalcular()}>
                Recalcular el aislado
              </Boton>
            </div>
          )}
        </div>
      )}
      {!soloRespaldo && (
        <div className="pila pila--chica">
          {bateriasEnTarjetas && <EquipoPorOpcion r={r} parametros={b.parametros} puedeEditar={puedeEditar} aislado={esAislado} />}
          <div className="opciones">
            {r.opciones.map((o) => (
              <div key={o.id} className={['opciones__celda', opcionVista?.id === o.id ? 'es-visible' : ''].join(' ')}>
                <TarjetaOpcion
                  o={o}
                  r={r}
                  parametros={b.parametros}
                  puedeEditar={puedeEditar}
                  // ADENDA z24: fuera de multisitio, baterías, aislado y ampliación a su inversor actual
                  aislado={b.cuestionario.tipo === 'aislado'}
                  invExactaDisponible={!cot.multisitio && !r.respaldo && b.cuestionario.tipo !== 'aislado' && r.opciones.some((x) => x.id === 'exacta') && !(b.cuestionario.ampliacion.es_ampliacion && b.cuestionario.ampliacion.existente?.conexion === 'inversor_actual')}
                />
              </div>
            ))}
          </div>
          <LeyendaFinsus r={r} finsusEncendido={b.parametros.formas_de_pago.finsus} puedeEditar={puedeEditar} />
        </div>
      )}

      {recorte && <TarjetaRecorte r={r} parametros={b.parametros} puedeEditar={puedeEditar} />}

      {r.respaldo && !bateriasEnTarjetas && !esAislado && <NivelesRespaldo r={r} parametros={b.parametros} puedeEditar={puedeEditar} />}

      {!soloRespaldo && opcionVista && <Grafica r={r} opcion={opcionVista} />}

      <RevisionIA />
      {puedeEditar && <Parametros parametros={b.parametros} tipo={b.cuestionario.tipo} fiscal={b.cuestionario.fiscal ?? null} r={r} />}

      <details className="plegable">
        <summary>
          <ChevronDown aria-hidden className="plegable__flecha" />
          Supuestos del cálculo ({r.version_motor}, {fechaHoraCorta(r.calculado_en)})
        </summary>
        <ul className="lista-simple">
          {r.supuestos.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
